/// <reference path="../../types/index.d.ts" />

import { $dom } from './DOM';
import { Finder } from './Finder';
import { Action } from '../models/Action';
import { RagsObject } from '../models/Object';
import { Character } from '../models/Character';
import { Room } from '../models/Room';
import { Variable } from '../models/Variable';
import { CustomProperty } from '../models/CustomProperty';
import { Check } from '../models/Check';
import { Command } from '../models/Command';
import { Condition } from '../models/Condition';
import { Settings } from '../core/Settings';
import { gameController } from '../core/GameController';
import { ENGINE_LOGGING_ENABLED, CurrentRoomGuid, VoidRoomGuid, SelfObjectGuid } from './Constants';
import { buildCommandSpecs, type ApiCommandSpec } from '../game/AdvancedApiSpec';

declare let TheGame: any;
declare let Globals: any;

// Forward declarations for functions defined in other modules
declare function PerformTextReplacements(text: string, obj: any, newval?: string): string;

declare function GetGameImage(imageName: string): any;

// Re-export functions from other modules
export { objecttostring, objectToString } from '../models/Object';
export { CharToString } from '../models/Character';
export { ResetLoopObjects, RestoreLoopObject } from '../core/Globals';

// Implement GetDestinationRoomName and roomDisplayName
export function GetDestinationRoomName(CurDirection: string): string | undefined {
	if (!TheGame) return undefined;
	Globals.movingDirection = CurDirection;
	const CurrentRoom = Finder.room(TheGame.Player.CurrentRoom);
	if (CurrentRoom != null) {
		for (let i = 0; i < CurrentRoom.Exits.length; i++) {
			if (CurrentRoom.Exits[i].Direction == CurDirection) {
				return CurrentRoom.Exits[i].DestinationRoom;
			}
		}
	}
	return undefined;
}

export function roomDisplayName(room: Room | undefined): string {
	if (!room) return '';
	return room.SDesc || room.Name;
}

export function isLocalFileContext(): boolean {
	if (typeof location === 'undefined') return false;
	return location.protocol === 'file:' || location.origin === 'null';
}

declare namespace GameUI {
	function refreshPanelItems(): void;

	function displayActions(obj: any, clickEvent: MouseEvent): void;

	function getDarkColor(color: string): string;
}
declare var ImageRecorder: any;
declare var ActionRecorder: any;

/**
 * Interactables - helper functions to get game objects
 */
export const Interactables = {
	inventoryObjects: function (): RagsObject[] {
		if (!TheGame || !TheGame.Objects) return [];
		return TheGame.Objects.filter((obj: any) => {
			return obj.locationtype == 'LT_PLAYER';
		});
	},

	visibleInventoryObjects: function (): RagsObject[] {
		return this.inventoryObjects().filter((obj) => {
			return obj.bVisible;
		});
	},

	allVisibleInventoryObjects: function (): RagsObject[] {
		const results: RagsObject[] = [];
		this.inventoryObjects().forEach((obj) => {
			if (!obj.bVisible) return;
			results.push(obj);

			if (obj.bContainer) {
				if (!obj.bOpenable || obj.bOpen) {
					results.push(...this.nestedObjects(obj));
				}
			}
		});
		return results;
	},

	roomObjects: function (): RagsObject[] {
		if (!TheGame || !TheGame.Objects) return [];
		return TheGame.Objects.filter((obj: any) => {
			return obj.locationtype == 'LT_ROOM' && obj.locationname == TheGame!.Player.CurrentRoom;
		});
	},

	visibleRoomObjects: function (): RagsObject[] {
		return this.roomObjects().filter((obj) => {
			return obj.bVisible;
		});
	},

	roomAndInventoryObjects: function (): RagsObject[] {
		const results: RagsObject[] = [];
		TheGame.Objects.forEach((obj) => {
			if (!obj.bVisible) return;
			if ((obj.locationtype == 'LT_ROOM' && obj.locationname == TheGame!.Player.CurrentRoom) ||
					(obj.locationtype == 'LT_PLAYER')) {
				results.push(obj);

				if (obj.bContainer) {
					if (!obj.bOpenable || obj.bOpen) {
						results.push(...this.nestedObjects(obj));
					}
				}
			}
		});
		return results;
	},

	characters: function (): Character[] {
		if (!TheGame || !TheGame.Characters) return [];
		return TheGame.Characters.filter((obj: any) => {
			return obj.CurrentRoom == TheGame!.Player.CurrentRoom;
		});
	},

	characterObjects: function (character: Character): RagsObject[] {
		if (!TheGame || !TheGame.Objects) return [];
		return TheGame.Objects.filter((obj: any) => {
			return obj.bVisible && obj.locationtype == 'LT_CHARACTER' && obj.locationname == character.Charname;
		});
	},

	nestedObjects: function (outerObject: RagsObject): RagsObject[] {
		const objects: RagsObject[] = [];
		TheGame.Objects.forEach((innerObject: any) => {
			if (!objectContainsObject(outerObject, innerObject)) return;
			// objectContainsObject() only passes for visible objects

			objects.push(innerObject);

			if (innerObject.bOpenable && innerObject.bOpen) {
				objects.push(...this.nestedObjects(innerObject));
			}
		});
		return objects;
	},
};

/**
 * Escape quoted tags in strings
 */
export function escapeQuotedTags(str: string): string {
	let insideQuote = false;
	const result: string[] = [];
	const quoteContent: string[] = [];
	for (let i = 0; i < str.length; i++) {
		const thisChr = str[i];
		const prevChr = str[i - 1];
		if (insideQuote) {
			quoteContent.push(thisChr);
			if (thisChr === '\n') {
				quoteContent[quoteContent.length - 1] = '&lt;br&gt;';
			} else if (thisChr === '"' && prevChr != '\\') {
				result.push(...quoteContent
					.join('')
					.replace(new RegExp('<\s*\/br\s*>', 'g'), '&lt;br&gt;')
					.replace(new RegExp('<\s*br\s*>', 'g'), '&lt;br&gt;')
					.split(''));
				insideQuote = false;
				quoteContent.length = 0;
			}
		} else if (thisChr === '"' && prevChr != '\\') {
			insideQuote = true;
			quoteContent.push(thisChr);
		} else {
			result.push(thisChr);
		}
	}
	return result.join('');
}

// Diagnostic-only wrapper: `with(scope)` makes the JS engine ask `scope` about
// every free identifier a script references before falling through to the
// real global scope. Wrapping `scope` in this Proxy does not change that
// resolution at all (the `has` trap always returns the same answer
// Reflect.has would) — it only lets us notice, via a console warning,
// whenever a script references a name that isn't on `scope` AND isn't a real
// global either, meaning the reference would fail today regardless. That's
// the signature of a script written expecting some ambient/shared JS binding
// this engine doesn't provide. Leave this in place; it's inert unless that
// happens.
// 'with(scope)' becomes the FIRST place the engine checks for every free
// identifier textually inside its block — including references to the
// enclosing function's OWN parameters (like the plumbing names below), not
// just names the executed script itself references. These reserved names
// must never trigger the diagnostic below: they always resolve correctly one
// level further out, but naively checking only scope+globalThis would flag
// them as "unresolved" on every single execution, drowning out any real
// signal from the executed script.
const RESERVED_SCOPE_DIAGNOSTIC_NAMES = new Set(['__ragsScope', '__ragsCode']);

function withScopeDiagnostics(scope: Record<string, unknown>, logPrefix: string): Record<string, unknown> {
    return new Proxy(scope, {
        has(target, prop) {
            const found = Reflect.has(target, prop);
            if (!found && typeof prop === 'string' && !RESERVED_SCOPE_DIAGNOSTIC_NAMES.has(prop) && !(prop in globalThis)) {
                console.warn(`${logPrefix} Action script referenced identifier "${prop}", which is not defined anywhere (no ambient scope binding, and no such global). If this identifier was meant to carry a value between separate script executions, that is not supported — the reference will fail exactly as it did before this diagnostic was added.`);
            }
            return found;
        },
    });
}

// Diagnostic-only wrapper for the OTHER half of the same question: since
// `has` returning false sends an assignment to an undeclared bare name (e.g.
// "Dice = 5" with no var/let/const) straight past `scope` to the real global
// object — which, in non-strict eval, silently creates a new global property
// instead of throwing — that's a genuine mechanism by which a later, separate
// script execution COULD read the value back, since this JS realm (window,
// worker, or sandboxed iframe) persists across every script run in a session.
// This snapshots the global object's own keys before and after running the
// script and warns about anything new, without altering what actually ran.
function detectImplicitGlobals<T>(logPrefix: string, run: () => T): T {
    const before = new Set(Reflect.ownKeys(globalThis));
    try {
        return run();
    } finally {
        for (const key of Reflect.ownKeys(globalThis)) {
            if (!before.has(key) && typeof key === 'string') {
                console.warn(`${logPrefix} Action script created a new global variable "${key}" via an undeclared assignment (e.g. "${key} = ...") rather than going through a proper RAGS variable. Because this execution context persists across script runs within the session, a later script referencing "${key}" as a bare identifier would read this value back, as if it were shared/carried-over state.`);
            }
        }
    }
}

function executeCode(code: string, scope = {}) {
    return detectImplicitGlobals('[Regalia main-thread executor]', () =>
        new Function('__ragsScope', '__ragsCode', 'with (__ragsScope) { return eval(__ragsCode); }')(withScopeDiagnostics(scope, '[Regalia main-thread executor]'), code)
    );
}

function normalizeJankyJavascript(str: string): string {
	// Need to remove newlines in string literals like 'my_str= "foo\nbar"'
	// but <br> tags as exported from RAGS should be treated as real newlines in the source,
	// so statements like "Foo()<br>Bar()" execute correctly.

	let escapedStr = escapeQuotedTags(str)
		.replace(new RegExp('</br>', 'g'), '')
		.replace(new RegExp('\n', 'g'), '')
		.replace(new RegExp('<br>', 'g'), '\n')
		.replace(new RegExp('<br/>', 'g'), '\n')
		.replace(new RegExp('&lt;br&gt;', 'g'), '<br>');

	// Strip var/let/const from all statements so they become plain assignments,
	// which work correctly inside a with-block eval.
	let newExpr = escapedStr.replace(/(;\s*)+$/, '').trim();
	newExpr = newExpr.replace(/\bvar\s+/g, '').replace(/\blet\s+/g, '').replace(/\bconst\s+/g, '');
	return newExpr;
}

/**
 * Evaluate JavaScript code with special handling for RAGS format
 */
export function evalJankyJavascript(str: string): any {
	const escapedStr = normalizeJankyJavascript(str);
	try {
		if (escapedStr.trim().length === 0) return undefined;
		const result = executeCode(escapedStr);
		return result !== undefined ? result : 'Null value returned from evaluate.';
	} catch (ex) {
		console.log(`Evaluated expression: ${escapedStr}`);
		throw ex;
	}
}

const IFRAME_MESSAGE_CHANNEL = 'regalia-advanced-exec';
const IFRAME_EXECUTOR_SOURCE = `<!doctype html>
<html>
<head><meta charset="utf-8"></head>
<body>
<script>
(function () {
  const AsyncFunction = Object.getPrototypeOf(async function () { }).constructor;
  const pendingRpc = new Map();
  let nextRpcId = 1;
  let cachedApiSource = null;
  let cachedApiFactory = null;

  function serializeError(err) {
    if (!err) return { name: 'Error', message: 'Unknown error' };
    return { name: err.name || 'Error', message: err.message || String(err) };
  }

  // IMPORTANT: keep the policy object itself, not just its .createScript method.
  // TrustedTypePolicy#createScript is a native, receiver-checked ("branded")
  // method: extracting it into a bare variable and calling it detached from
  // the policy instance (as the old code did) throws "TypeError: Illegal
  // invocation" on every call, exactly like detaching console.log or
  // postMessage from their owning object.
  var _regaliaEvalPolicy = typeof window !== 'undefined' && typeof window.trustedTypes !== 'undefined'
      ? window.trustedTypes.createPolicy('regalia-eval', { createScript: function(s) { return s; } })
      : null;

  // 'with(scope)' becomes the FIRST place the engine checks for every free
  // identifier textually inside its block — including references to the
  // enclosing function's OWN parameters (the plumbing names below), not just
  // names the executed script itself references. These reserved names must
  // never trigger the diagnostic: they always resolve correctly one level
  // further out, but naively checking only scope+globalThis would flag them
  // as "unresolved" on every single execution, drowning out any real signal.
  var RESERVED_SCOPE_DIAGNOSTIC_NAMES = new Set(['__ragsScope', '__ragsCode']);

  // Diagnostic-only wrapper: does not change what 'with(scope)' resolves,
  // only warns (via this iframe's own console) when a script references an
  // identifier that is neither on scope nor a real global — i.e. a reference
  // that fails today regardless, which is the signature of a script expecting
  // some ambient/shared JS binding this engine does not provide.
  function withScopeDiagnostics(scope) {
    return new Proxy(scope, {
      has: function (target, prop) {
        var found = Reflect.has(target, prop);
        if (!found && typeof prop === 'string' && !RESERVED_SCOPE_DIAGNOSTIC_NAMES.has(prop) && !(prop in globalThis)) {
          console.warn('[Regalia iframe executor] Action script referenced identifier "' + prop + '", which is not defined anywhere (no ambient scope binding, and no such global). If this identifier was meant to carry a value between separate script executions, that is not supported.');
        }
        return found;
      }
    });
  }

  // Diagnostic-only wrapper for the OTHER half of the same question: an
  // assignment to an undeclared bare name (e.g. "Dice = 5") is sent straight
  // past 'scope' to this sandboxed iframe's OWN global object, which (in
  // non-strict eval) silently creates a new global property instead of
  // throwing. Since this iframe is cached and reused across every script run
  // in the session, that's a genuine mechanism by which a later script could
  // read the value back. This snapshots the iframe's global own keys before
  // and after running the script and warns about anything new, without
  // altering what actually ran.
  function detectImplicitGlobals(logPrefix, run) {
    var before = new Set(Reflect.ownKeys(globalThis));
    try {
      return run();
    } finally {
      var after = Reflect.ownKeys(globalThis);
      for (var i = 0; i < after.length; i++) {
        var key = after[i];
        if (!before.has(key) && typeof key === 'string') {
          console.warn(logPrefix + ' Action script created a new global variable "' + key + '" via an undeclared assignment rather than going through a proper RAGS variable. Because this sandboxed context persists across script runs within the session, a later script referencing "' + key + '" as a bare identifier would read this value back, as if it were shared/carried-over state.');
        }
      }
    }
  }

  function executeJanky(code) {
    var script = _regaliaEvalPolicy ? _regaliaEvalPolicy.createScript(code) : code;
    // 'script' must be threaded in as an actual parameter, not referenced by
    // name from inside the function body string: functions created via the
    // Function constructor do NOT close over the surrounding scope, so a body
    // that merely mentions an outer variable by name throws
    // "ReferenceError: script is not defined" the moment it runs. The
    // parameter names are deliberately reserved/mangled (see
    // RESERVED_SCOPE_DIAGNOSTIC_NAMES above) so the diagnostic proxy never
    // mistakes this internal plumbing for an unresolved script identifier.
    return detectImplicitGlobals('[Regalia iframe executor]', function () {
      return Function('__ragsScope', '__ragsCode', 'with (__ragsScope) { return eval(__ragsCode); }')(withScopeDiagnostics({}), script);
    });
  }

  function postToParent(message) {
    parent.postMessage(Object.assign({ __regalia: '${IFRAME_MESSAGE_CHANNEL}' }, message), '*');
  }

  function callRpc(method, args, context) {
    const id = nextRpcId++;
    return new Promise((resolve, reject) => {
      pendingRpc.set(id, { resolve, reject });
      postToParent({ type: 'rpc', id, method, args, context });
    });
  }

  function wrapAdvancedCode(code, globals) {
    const names = Object.keys(globals || {}).filter((name) => typeof globals[name] === 'function');
    if (names.length === 0) return code;
    const selfLine = "const self = (typeof getSelf === 'function') ? await getSelf() : undefined;\\n";
    return 'const { ' + names.join(', ') + ' } = rags;\\n' + selfLine + code;
  }

  function getApiFactory(apiSource) {
    if (!apiSource) {
      throw new Error('Missing advanced API source');
    }
    if (apiSource !== cachedApiSource) {
      cachedApiSource = apiSource;
      cachedApiFactory = new Function(
        'callRpc',
        'apiSpec',
        'context',
        apiSource + '\\nreturn createAdvancedApi(context, callRpc, apiSpec);'
      );
    }
    return cachedApiFactory;
  }

  window.addEventListener('message', async (event) => {
    const data = (event && event.data) || {};
    if (!data || data.__regalia !== '${IFRAME_MESSAGE_CHANNEL}') return;

    if (data.type === 'rpcResult') {
      const pending = pendingRpc.get(data.id);
      if (!pending) return;
      pendingRpc.delete(data.id);
      if (data.error) {
        const err = new Error(data.error.message || String(data.error));
        err.name = data.error.name || 'Error';
        pending.reject(err);
        return;
      }
      pending.resolve(data.result);
      return;
    }

    const id = data.id;
    const code = data.code;
    if (!id || !code) return;
    try {
      if (data.mode === 'advanced-global') {
        const apiFactory = getApiFactory(data.apiSource);
        const api = apiFactory((method, args) => callRpc(method, args, data.context), data.apiSpec || {}, data.context);
        const rags = Object.assign({}, api.globals);
        const globalTarget = (typeof globalThis !== 'undefined') ? globalThis : window;
        Object.assign(globalTarget, rags);
        globalTarget.rags = rags;
        try {
          globalTarget.self = (typeof rags.getSelf === 'function') ? await rags.getSelf() : undefined;
        } catch {
          globalTarget.self = undefined;
        }
        const globalEval = globalTarget.eval || eval;
        const scripts = Array.isArray(code) ? code : [code];
        for (const script of scripts) {
          if (!script || !String(script).trim()) continue;
          globalEval(String(script));
        }
        postToParent({ id, result: true });
        return;
      }
      if (data.mode === 'advanced') {
        const apiFactory = getApiFactory(data.apiSource);
        const api = apiFactory((method, args) => callRpc(method, args, data.context), data.apiSpec || {}, data.context);
        const rags = Object.assign({}, api.globals);
        const wrapped = wrapAdvancedCode(code, api.globals);
        const fn = new AsyncFunction('rags', wrapped);
        const result = await fn(rags);
        postToParent({ id, result });
        return;
      }

      const result = executeJanky(code);
      postToParent({ id, result });
    } catch (err) {
      // Log inside the iframe's OWN console context first: only {name, message}
      // strings survive the postMessage trip back to the parent, so the
      // parent's reconstructed Error necessarily has a fresh, misleading stack
      // (pointing at wherever the parent happened to call "new Error(...)",
      // not the real failure site). This is the real stack trace, in the real
      // execution context, for the sandboxed ("janky"/legacy JS) evaluator.
      console.error('[Regalia iframe executor]', data.mode || 'janky', 'code:', code, err);
      postToParent({ id, error: serializeError(err) });
    }
  });

  postToParent({ type: 'ready' });
})();
</script>
</body>
</html>`;

const TURN_SEPARATOR_SELECTOR = 'hr.turn-separator';

class JankyJavascriptExecutor {
	private worker: Worker | null = null;
	private pending = new Map<number, { resolve: (value: any) => void; reject: (err: any) => void }>();
	private nextId = 1;
	private readonly workerUrl = './regalia/worker.js';
	private iframe: HTMLIFrameElement | null = null;
	private iframeReady = false;
	private iframeReadyPromise: Promise<void> | null = null;
	private iframeReadyResolver: (() => void) | null = null;
	private iframeMessageListener: ((event: MessageEvent) => void) | null = null;
	private useIframeFallback = false;
	private rpcHandler: ((request: { method: string; args: any[]; context?: any }) => Promise<any>) | null = null;

	private ensureWorker(): Worker {
		if (this.worker) return this.worker;
		const worker = new Worker(this.workerUrl);
		worker.onmessage = (event: MessageEvent) => {
			const data = (event && event.data) || {};
			// Defensive: any exception thrown while processing a completion message
			// (e.g. an unexpected/cross-realm shape on `data.error`, seen in the wild
			// as "TypeError: Illegal invocation") must not escape as an uncaught
			// exception. Left unguarded, it can leave the matching `pending` promise
			// permanently unsettled, silently hanging whatever command awaited it
			// (e.g. a Variable: Set (JavaScript) command mid room-enter events).
			try {
				if (data.type === 'rpc') {
					if (!this.rpcHandler) {
						worker.postMessage({
							type: 'rpcResult',
							id: data.id,
							error: { name: 'Error', message: 'No RPC handler registered' }
						});
						return;
					}
					Promise.resolve(this.rpcHandler({
						method: data.method,
						args: data.args || [],
						context: data.context
					}))
						.then((result) => {
							worker.postMessage({ type: 'rpcResult', id: data.id, result });
						})
						.catch((err: any) => {
							console.error('[JankyJavascriptExecutor] rpc handler failed', data.method, err);
							worker.postMessage({
								type: 'rpcResult',
								id: data.id,
								error: { name: err?.name || 'Error', message: err?.message || String(err) }
							});
						});
					return;
				}
				const pending = this.pending.get(data.id);
				if (!pending) return;
				this.pending.delete(data.id);
				if (data.error) {
					// Build the rejection from plain strings only; never forward
					// `data.error` itself (it crossed a postMessage/realm boundary and
					// may not behave like a normal same-realm object).
					const message = typeof data.error?.message === 'string' ? data.error.message : String(data.error);
					const err = new Error(message);
					(err as any).name = typeof data.error?.name === 'string' ? data.error.name : 'Error';
					pending.reject(err);
					return;
				}
				pending.resolve(data.result);
			} catch (ex: any) {
				console.warn('[JankyJavascriptExecutor] worker message handling failed', ex);
				const pending = this.pending.get(data.id);
				if (pending) {
					this.pending.delete(data.id);
					pending.reject(new Error('Script execution failed: ' + (ex?.message || String(ex))));
				}
			}
		};
		worker.onerror = (event: ErrorEvent) => {
			const err = event?.error || new Error(event?.message || 'Worker error');
			this.rejectAll(err);
		};
		this.worker = worker;
		return worker;
	}

	private ensureIframe(): HTMLIFrameElement | null {
		if (typeof document === 'undefined') return null;
		if (this.iframe) return this.iframe;
		const iframe = document.createElement('iframe');
		iframe.setAttribute('sandbox', 'allow-scripts allow-same-origin');
		iframe.srcdoc = IFRAME_EXECUTOR_SOURCE;
		iframe.style.display = 'none';
		document.body.appendChild(iframe);
		this.iframe = iframe;
		this.iframeReady = false;
		this.iframeReadyPromise = new Promise((resolve) => {
			this.iframeReadyResolver = resolve;
		});
		if (!this.iframeMessageListener && typeof window !== 'undefined') {
			this.iframeMessageListener = (event: MessageEvent) => {
				if (!this.iframe || event.source !== this.iframe.contentWindow) return;
				const data = (event && event.data) || {};
				if (!data || data.__regalia !== IFRAME_MESSAGE_CHANNEL) return;
				// Defensive: this listener is registered on `window` for the lifetime
				// of the executor, so any uncaught exception here (observed in the
				// wild as "TypeError: Illegal invocation" when handling a cross-realm
				// `data.error` value from the sandboxed iframe) would otherwise escape
				// as an unhandled window error and leave the matching `pending`
				// promise unsettled forever — hanging whatever awaited it (e.g. a
                                // Variable: Set (JavaScript) command mid room-enter events).
				try {
					if (data.type === 'ready') {
						this.iframeReady = true;
						this.iframeReadyResolver?.();
						return;
					}
					if (data.type === 'rpc') {
						if (!this.rpcHandler) {
							this.postMessageToIframe({
								type: 'rpcResult',
								id: data.id,
								error: { name: 'Error', message: 'No RPC handler registered' }
							});
							return;
						}
						Promise.resolve(this.rpcHandler({
							method: data.method,
							args: data.args || [],
							context: data.context
						}))
							.then((result) => {
								this.postMessageToIframe({ type: 'rpcResult', id: data.id, result });
							})
							.catch((err: any) => {
								console.error('[JankyJavascriptExecutor] rpc handler failed', data.method, err);
								this.postMessageToIframe({
									type: 'rpcResult',
									id: data.id,
									error: { name: err?.name || 'Error', message: err?.message || String(err) }
								});
							});
						return;
					}
					const pending = this.pending.get(data.id);
					if (!pending) return;
					this.pending.delete(data.id);
					if (data.error) {
						// Build the rejection from plain strings only; never forward
						// `data.error` itself (it crossed a postMessage/realm boundary
						// from the sandboxed iframe and may not behave like a normal
						// same-realm object).
						const message = typeof data.error?.message === 'string' ? data.error.message : String(data.error);
						const err = new Error(message);
						(err as any).name = typeof data.error?.name === 'string' ? data.error.name : 'Error';
						pending.reject(err);
						return;
					}
					pending.resolve(data.result);
				} catch (ex: any) {
					console.warn('[JankyJavascriptExecutor] iframe message handling failed', ex);
					const pending = this.pending.get(data.id);
					if (pending) {
						this.pending.delete(data.id);
						pending.reject(new Error('Script execution failed: ' + (ex?.message || String(ex))));
					}
				}
			};
			window.addEventListener('message', this.iframeMessageListener);
		}
		return iframe;
	}

	private shouldUseIframeFallback(): boolean {
		if (this.useIframeFallback) return true;
		if (typeof location !== 'undefined') {
			if (location.protocol === 'file:' || location.origin === 'null') {
				return true;
			}
		}
		return false;
	}

	private rejectAll(err: any): void {
		this.pending.forEach((pending) => pending.reject(err));
		this.pending.clear();
		if (this.worker) {
			this.worker.terminate();
			this.worker = null;
		}
	}

	private async evaluateInIframe(code: string): Promise<any> {
		const iframe = await this.ensureIframeReady();
		if (!iframe || !iframe.contentWindow) {
			// Last resort fallback if iframe cannot be created.
			return evalJankyJavascript(code);
		}
		const id = this.nextId++;
		return new Promise((resolve, reject) => {
			this.pending.set(id, { resolve, reject });
			this.postMessageToIframe({ id, code });
		});
	}

	async evaluate(code: string): Promise<any> {
		if (this.shouldUseIframeFallback() || typeof Worker === 'undefined') {
			return this.evaluateInIframe(code);
		}
		let worker: Worker;
		try {
			worker = this.ensureWorker();
		} catch (err) {
			this.useIframeFallback = true;
			return this.evaluateInIframe(code);
		}
		const id = this.nextId++;
		return new Promise((resolve, reject) => {
			this.pending.set(id, { resolve, reject });
			worker.postMessage({ id, code });
		});
	}

	private async evaluateAdvancedInIframe(code: string, context: any, rpcHandler: (request: any) => Promise<any>): Promise<any> {
		const iframe = await this.ensureIframeReady();
		if (!iframe || !iframe.contentWindow) {
			// Last resort fallback if iframe cannot be created.
			const AsyncFunction = Object.getPrototypeOf(async function () { }).constructor;
			const api = createAdvancedApi(context, rpcHandler, ADVANCED_API_SPEC);
			const rags = { ...api.globals };
			const wrapped = wrapAdvancedCode(code, api.globals);
			const fn = new AsyncFunction('rags', wrapped);
			return fn(rags);
		}
		const previousHandler = this.rpcHandler;
		this.rpcHandler = rpcHandler;
		const id = this.nextId++;
		const apiSpec = ADVANCED_API_SPEC;
		const apiSource = ADVANCED_API_SOURCE;
		return new Promise((resolve, reject) => {
			this.pending.set(id, { resolve, reject });
			this.postMessageToIframe({ id, code, mode: 'advanced', context, apiSpec, apiSource });
		}).finally(() => {
			this.rpcHandler = previousHandler;
		});
	}

	async evaluateAdvanced(code: string, context: any, rpcHandler: (request: any) => Promise<any>): Promise<any> {
		if (this.shouldUseIframeFallback() || typeof Worker === 'undefined') {
			return this.evaluateAdvancedInIframe(code, context, rpcHandler);
		}
		let worker: Worker;
		try {
			worker = this.ensureWorker();
		} catch (err) {
			this.useIframeFallback = true;
			return this.evaluateAdvancedInIframe(code, context, rpcHandler);
		}

		const previousHandler = this.rpcHandler;
		this.rpcHandler = rpcHandler;
		const id = this.nextId++;
		const apiSpec = ADVANCED_API_SPEC;
		const apiSource = ADVANCED_API_SOURCE;
		return new Promise((resolve, reject) => {
			this.pending.set(id, { resolve, reject });
			worker.postMessage({ id, code, mode: 'advanced', context, apiSpec, apiSource });
		}).finally(() => {
			this.rpcHandler = previousHandler;
		});
	}

	private async evaluateAdvancedGlobalsInIframe(scripts: string[], context: any, rpcHandler: (request: any) => Promise<any>): Promise<any> {
		const iframe = await this.ensureIframeReady();
		if (!iframe || !iframe.contentWindow) {
			const api = createAdvancedApi(context, rpcHandler, ADVANCED_API_SPEC);
			const rags = { ...api.globals };
			const globalTarget = globalThis as any;
			Object.assign(globalTarget, rags);
			globalTarget.rags = rags;
			try {
				globalTarget.self = typeof rags.getSelf === 'function' ? await rags.getSelf() : undefined;
			} catch {
				globalTarget.self = undefined;
			}
			const globalEval = globalTarget.eval || eval;
			for (const script of scripts) {
				if (!script || !String(script).trim()) continue;
				globalEval(String(script));
			}
			return true;
		}
		const previousHandler = this.rpcHandler;
		this.rpcHandler = rpcHandler;
		const id = this.nextId++;
		const apiSpec = ADVANCED_API_SPEC;
		const apiSource = ADVANCED_API_SOURCE;
		return new Promise((resolve, reject) => {
			this.pending.set(id, { resolve, reject });
			this.postMessageToIframe({ id, code: scripts, mode: 'advanced-global', context, apiSpec, apiSource });
		}).finally(() => {
			this.rpcHandler = previousHandler;
		});
	}

	async evaluateAdvancedGlobals(scripts: string[], context: any, rpcHandler: (request: any) => Promise<any>): Promise<any> {
		if (this.shouldUseIframeFallback() || typeof Worker === 'undefined') {
			return this.evaluateAdvancedGlobalsInIframe(scripts, context, rpcHandler);
		}
		let worker: Worker;
		try {
			worker = this.ensureWorker();
		} catch (err) {
			this.useIframeFallback = true;
			return this.evaluateAdvancedGlobalsInIframe(scripts, context, rpcHandler);
		}

		const previousHandler = this.rpcHandler;
		this.rpcHandler = rpcHandler;
		const id = this.nextId++;
		const apiSpec = ADVANCED_API_SPEC;
		const apiSource = ADVANCED_API_SOURCE;
		return new Promise((resolve, reject) => {
			this.pending.set(id, { resolve, reject });
			worker.postMessage({ id, code: scripts, mode: 'advanced-global', context, apiSpec, apiSource });
		}).finally(() => {
			this.rpcHandler = previousHandler;
		});
	}

	reset(): void {
		this.rejectAll(new Error('Janky JS executor reset'));
		if (this.iframe) {
			this.iframe.remove();
			this.iframe = null;
		}
		if (this.iframeMessageListener && typeof window !== 'undefined') {
			window.removeEventListener('message', this.iframeMessageListener);
			this.iframeMessageListener = null;
		}
		this.iframeReady = false;
		this.iframeReadyPromise = null;
		this.iframeReadyResolver = null;
		this.useIframeFallback = false;
	}

	private async ensureIframeReady(): Promise<HTMLIFrameElement | null> {
		const iframe = this.ensureIframe();
		if (!iframe) return null;
		if (this.iframeReadyPromise && !this.iframeReady) {
			await this.iframeReadyPromise;
		}
		return iframe;
	}

	private postMessageToIframe(message: any): void {
		if (!this.iframe || !this.iframe.contentWindow) {
			throw new Error('Iframe executor not available');
		}
		this.iframe.contentWindow.postMessage({ ...message, __regalia: IFRAME_MESSAGE_CHANNEL }, '*');
	}
}

let jankyJavascriptExecutor: JankyJavascriptExecutor | null = null;

export function getJankyJavascriptExecutor(): JankyJavascriptExecutor {
	if (!jankyJavascriptExecutor) {
		jankyJavascriptExecutor = new JankyJavascriptExecutor();
	}
	return jankyJavascriptExecutor;
}

export function resetJankyJavascriptExecutor(): void {
	if (jankyJavascriptExecutor) {
		jankyJavascriptExecutor.reset();
	}
	jankyJavascriptExecutor = null;
}

export async function evalJankyJavascriptAsync(str: string): Promise<any> {
	const escapedStr = normalizeJankyJavascript(str);
	try {
		if (escapedStr.trim().length === 0) return undefined;
		const result = await getJankyJavascriptExecutor().evaluate(escapedStr);
		return result !== undefined ? result : 'Null value returned from evaluate.';
	} catch (ex) {
		console.log(`Evaluated expression: ${escapedStr}`);
		throw ex;
	}
}

export async function evalLegacyCustomPropertyJavascriptAsync(str: string, currentValue: string): Promise<any> {
	const escapedStr = normalizeJankyJavascript(str);
	const trimmed = escapedStr.trim();
	if (trimmed.length === 0) return undefined;

	const currentValueExpr = JSON.stringify(currentValue ?? '');
	const compatibilityExpr = /^[+\-*/%]/.test(trimmed)
		? `${currentValueExpr} ${trimmed}`
		: trimmed;

	try {
		const result = await getJankyJavascriptExecutor().evaluate(compatibilityExpr);
		return result !== undefined ? result : 'Null value returned from evaluate.';
	} catch (ex) {
		console.log(`Evaluated expression: ${compatibilityExpr}`);
		throw ex;
	}
}

export async function evalAdvancedScriptAsync(code: string, context: any, rpcHandler: (request: any) => Promise<any>): Promise<any> {
	try {
		if (!code || code.trim().length === 0) return undefined;
		return await getJankyJavascriptExecutor().evaluateAdvanced(code, context, rpcHandler);
	} catch (ex) {
		console.log(`Advanced script failed`);
		throw ex;
	}
}

export async function evalAdvancedGlobalScripts(scripts: string[], context: any, rpcHandler: (request: any) => Promise<any>): Promise<void> {
	if (!Array.isArray(scripts) || scripts.length === 0) return;
	await getJankyJavascriptExecutor().evaluateAdvancedGlobals(scripts, context, rpcHandler);
}

function wrapAdvancedCode(code: string, globals: Record<string, any>): string {
	const names = Object.keys(globals).filter(name => typeof globals[name] === 'function');
	if (names.length === 0) return code;
	const selfLine = 'const self = (typeof getSelf === \'function\') ? await getSelf() : undefined;\n';
	return `const { ${names.join(', ')} } = rags;\n${selfLine}${code}`;
}

const COMMAND_SPECS: ApiCommandSpec[] = buildCommandSpecs();

const COMMAND_NAME_TEXT_TYPES = new Set<string>([
	'CT_DISPLAYTEXT',
	'CT_SETCHARDESC',
	'CT_DEBUGTEXT',
	'CT_COMMENT',
	'CT_JAVA_SET_RAGS',
	'CT_SETITEMDESC',
	'CT_SETPLAYERDESC',
	'CT_SETROOMDESCRIPTION',
	'CT_ENDGAME',
]);

const ROOM_REFERENCE_TYPES = new Set(['room', 'roomCurrent', 'roomNoSpecial', 'roomNoVoid', 'roomDestination']);
const OBJECT_REFERENCE_TYPES = new Set(['object', 'wearableObject', 'containerObject', 'lockableObject', 'openableObject']);

function resolveReference(value: any, type?: string): string {
	if (value == null) {
		if (type && ROOM_REFERENCE_TYPES.has(type)) return VoidRoomGuid;
		return '';
	}
	if (typeof value === 'string') {
		if (type && ROOM_REFERENCE_TYPES.has(type)) {
			if (value === '<CurrentRoom>' || value === CurrentRoomGuid) return CurrentRoomGuid;
			if (value === '<Void>' || value === '<None>' || value === 'None' || value === VoidRoomGuid) return VoidRoomGuid;
		}
		if (type && OBJECT_REFERENCE_TYPES.has(type)) {
			if (value === '<CurrentObject>' || value === SelfObjectGuid) return SelfObjectGuid;
		}
		return value;
	}
	if (typeof value === 'number') return String(value);
	const ref = (value as any).__ragsRef;
	if (ref && typeof ref.id === 'string') return ref.id;
	if ((value as any).id != null) return String((value as any).id);
	if (type && ROOM_REFERENCE_TYPES.has(type)) {
		return (value as any).UniqueID || (value as any).Name || '';
	}
	if (type && OBJECT_REFERENCE_TYPES.has(type)) {
		return (value as any).UniqueIdentifier || (value as any).name || '';
	}
	if (type === 'character') {
		return (value as any).Charname || (value as any).Name || '';
	}
	if (type === 'timer') {
		return (value as any).Name || '';
	}
	if (type === 'variable') {
		return (value as any).varname || (value as any).Name || '';
	}
	if (type === 'mediaImage' || type === 'image' || type === 'media' || type === 'mediaAudio' || type === 'mediaCompass') {
		return (value as any).filename || (value as any).name || '';
	}
	return String(value);
}

function mapTextStyleToEnhInput(data: any): any {
	if (!data || typeof data !== 'object') return null;
	const payload: any = {};
	if (data.backgroundColor != null) payload.backgroundColor = String(data.backgroundColor);
	if (data.textColor != null) payload.textColor = String(data.textColor);
	if (data.imageName != null) payload.imageName = String(data.imageName);
	if (data.newImage != null) payload.newImage = String(data.newImage);
	if (data.textFont != null) payload.textFont = String(data.textFont);
	if (data.useEnhancedGraphics != null) payload.useEnhancedGraphics = Boolean(data.useEnhancedGraphics);
	if (data.allowCancel != null) payload.allowCancel = Boolean(data.allowCancel);
	return payload;
}

function buildCommandPayload(spec: ApiCommandSpec, args: any, textCommandTypes: Set<string>): any {
	const payload: any = {};
	let input: any = {};
	if (args && typeof args === 'object' && !Array.isArray(args)) {
		input = args;
	} else if (args !== undefined && args !== null && spec.params && spec.params.length > 0) {
		input = { [spec.params[0].key]: args };
	}
	for (const param of spec.params) {
		if (!(param.key in input)) continue;
		const raw = input[param.key];
		const value = param.type ? resolveReference(raw, param.type) : resolveReference(raw);
		if (param.field === 'commandName') {
			payload.commandName = value;
			if (textCommandTypes.has(spec.commandType) && !payload.param1) {
				payload.param1 = value;
			}
		} else {
			payload[param.field] = value;
		}
	}
	if (input.customChoices && Array.isArray(input.customChoices)) {
		payload.customChoices = input.customChoices;
	}
	if (input.textStyle) {
		const enh = mapTextStyleToEnhInput(input.textStyle);
		if (enh) payload.enhInputData = enh;
	}
	if (spec.commandType === 'CT_DISPLAYTEXT') {
		payload.allowHtml = input.allowHtml !== undefined ? Boolean(input.allowHtml) : true;
		payload.noParagraphWrapper = Boolean(input.noParagraphWrapper || input.suppressParagraph || input.rawHtml);
	}
	return payload;
}

function createAdvancedApi(
	context: any,
	rpcHandler: any,
	apiSpec?: { specs?: ApiCommandSpec[]; textCommandTypes?: string[]; constants?: any },
	constants?: {
		roomReferenceTypes?: string[];
		objectReferenceTypes?: string[];
		currentRoomGuid?: string;
		voidRoomGuid?: string;
		selfObjectGuid?: string;
	}
) {
	const specConstants = apiSpec && (apiSpec as any).constants ? (apiSpec as any).constants : {};
	const mergedConstants = { ...(specConstants || {}), ...(constants || {}) };
	const roomReferenceTypes = new Set(mergedConstants.roomReferenceTypes || ['room', 'roomCurrent', 'roomNoSpecial', 'roomNoVoid', 'roomDestination']);
	const objectReferenceTypes = new Set(mergedConstants.objectReferenceTypes || ['object', 'wearableObject', 'containerObject', 'lockableObject', 'openableObject']);
	const currentRoomGuid = mergedConstants.currentRoomGuid || '00000000-0000-0000-0000-000000000001';
	const voidRoomGuid = mergedConstants.voidRoomGuid || '00000000-0000-0000-0000-000000000002';
	const selfObjectGuid = mergedConstants.selfObjectGuid || '00000000-0000-0000-0000-000000000004';

	const callRpc = (method: string, args: any[]) => {
		if (typeof rpcHandler === 'function' && rpcHandler.length >= 2) {
			return rpcHandler(method, args, context);
		}
		return rpcHandler({ method, args, context });
	};
	const specs = apiSpec && Array.isArray(apiSpec.specs) ? apiSpec.specs : [];
	const textCommandTypes = new Set(apiSpec && Array.isArray(apiSpec.textCommandTypes) ? apiSpec.textCommandTypes : []);

	const resolveReference = (value: any, type?: string): string => {
		if (value == null) {
			if (type && roomReferenceTypes.has(type)) return voidRoomGuid;
			return '';
		}
		if (typeof value === 'string') {
			if (type && roomReferenceTypes.has(type)) {
				if (value === '<CurrentRoom>' || value === currentRoomGuid) return currentRoomGuid;
				if (value === '<Void>' || value === '<None>' || value === 'None' || value === voidRoomGuid) return voidRoomGuid;
			}
			if (type && objectReferenceTypes.has(type)) {
				if (value === '<CurrentObject>' || value === selfObjectGuid) return selfObjectGuid;
			}
			return value;
		}
		if (typeof value === 'number') return String(value);
		const ref = value && value.__ragsRef;
		if (ref && typeof ref.id === 'string') return ref.id;
		if (value && value.id != null) return String(value.id);
		if (type && roomReferenceTypes.has(type)) {
			return (value && (value.UniqueID || value.Name)) || '';
		}
		if (type && objectReferenceTypes.has(type)) {
			return (value && (value.UniqueIdentifier || value.name)) || '';
		}
		if (type === 'character') {
			return (value && (value.Charname || value.Name)) || '';
		}
		if (type === 'timer') {
			return (value && value.Name) || '';
		}
		if (type === 'variable') {
			return (value && (value.varname || value.Name)) || '';
		}
		if (type === 'mediaImage' || type === 'image' || type === 'media' || type === 'mediaAudio' || type === 'mediaCompass') {
			return (value && (value.filename || value.name)) || '';
		}
		return String(value);
	};

	const detectSelfTypeLocal = (value: any): string | null => {
		if (!value || typeof value !== 'object') return null;
		if (typeof value.Charname === 'string') return 'character';
		if (typeof value.UniqueIdentifier === 'string') return 'object';
		if (typeof value.UniqueID === 'string') return 'room';
		if (typeof value.TType === 'string' || typeof value.TimerSeconds === 'number') return 'timer';
		if (typeof value.PlayerGender === 'string' || typeof value.StartingRoom === 'string') return 'player';
		return null;
	};

	const mapTextStyleToEnhInput = (data: any): any => {
		if (!data || typeof data !== 'object') return null;
		const payload: any = {};
		if (data.backgroundColor != null) payload.backgroundColor = String(data.backgroundColor);
		if (data.textColor != null) payload.textColor = String(data.textColor);
		if (data.imageName != null) payload.imageName = String(data.imageName);
		if (data.newImage != null) payload.newImage = String(data.newImage);
		if (data.textFont != null) payload.textFont = String(data.textFont);
		if (data.useEnhancedGraphics != null) payload.useEnhancedGraphics = Boolean(data.useEnhancedGraphics);
		if (data.allowCancel != null) payload.allowCancel = Boolean(data.allowCancel);
		return payload;
	};

	const buildCommandPayload = (spec: ApiCommandSpec, args: any, textCommandTypesLocal: Set<string>): any => {
		const payload: any = {};
		let input: any = {};
		if (args && typeof args === 'object' && !Array.isArray(args)) {
			input = args;
		} else if (args !== undefined && args !== null && spec.params && spec.params.length > 0) {
			input = { [spec.params[0].key]: args };
		}
		if (spec.commandType === 'CT_DISPLAYTEXT' && spec.params && spec.params.length > 0) {
			const primaryKey = spec.params[0].key;
			if (!(primaryKey in input)) {
				if ('text' in input) {
					input[primaryKey] = input.text;
				} else if ('value' in input) {
					input[primaryKey] = input.value;
				}
			}
		}
		for (const param of spec.params) {
			if (!(param.key in input)) continue;
			const raw = input[param.key];
			const value = param.type ? resolveReference(raw, param.type) : resolveReference(raw);
			if (param.field === 'commandName') {
				payload.commandName = value;
				if (textCommandTypesLocal.has(spec.commandType) && !payload.param1) {
					payload.param1 = value;
				}
			} else {
				payload[param.field] = value;
			}
		}
		if (input.customChoices && Array.isArray(input.customChoices)) {
			payload.customChoices = input.customChoices;
		}
		if (input.textStyle) {
			const enh = mapTextStyleToEnhInput(input.textStyle);
			if (enh) payload.enhInputData = enh;
		}
		if (spec.commandType === 'CT_DISPLAYTEXT') {
			payload.allowHtml = input.allowHtml !== undefined ? Boolean(input.allowHtml) : true;
			payload.noParagraphWrapper = Boolean(input.noParagraphWrapper || input.suppressParagraph || input.rawHtml);
		}
		return payload;
	};

	const runCommand = (commandType: string, payload: any) => {
		return callRpc('command', [commandType, payload || {}])
			.then((result: any) => {
				if (result && result.stop) {
					const err = new Error('Advanced action stopped');
					(err as any).name = 'RAGS_STOP';
					throw err;
				}
				return result;
			});
	};

	const globals: Record<string, any> = {
		getLastInput: () => callRpc('getAdditionalData', []),
		requestInput: (options?: any) => callRpc('requestInput', [options || {}]),
		expandText: (text: string) => callRpc('expandText', [text]),
		compareDateTime: (actual: any, operator: string, expected: any) => callRpc('compareDateTime', [actual, operator, expected]),
		applyDateTimeOperation: (name: any, operation: string, value: any) => {
			const resolved = resolveReference(name, 'variable');
			return callRpc('applyDateTimeOperation', [resolved, operation || 'Equals', value]);
		},
		getGameInfo: () => callRpc('getGameInfo', []),
		getMediaList: () => callRpc('getMediaList', []),
		getMediaGroup: (groupName: string) => callRpc('getMediaGroup', [groupName || '']),
		log: (...args: any[]) => callRpc('log', args),
		sleep: (ms: number) => new Promise((resolve) => setTimeout(resolve, Math.max(0, Number(ms) || 0))),
		getCurrentRoom: () => callRpc('getCurrentRoom', []),
		getCurrentObject: () => callRpc('getCurrentObject', []),
		getMovingDirection: () => callRpc('getMovingDirection', []),
		getRoomById: (id: string) => callRpc('getRoom', [id]),
		getRoomByName: (name: string) => callRpc('getRoom', [name]),
		getObjectById: (id: string) => callRpc('getObject', [id]),
		getObjectByName: (name: string) => callRpc('getObject', [name]),
		getCharacterByName: (name: string) => callRpc('getCharacter', [name]),
		getTimerByName: (name: string) => callRpc('getTimer', [name]),
		getVariableByName: (name: string) => callRpc('getVariable', [name]),
		getPlayer: () => callRpc('getPlayer', []),
		getVariableValue: (name: any) => {
			const resolved = resolveReference(name, 'variable');
			return callRpc('getVariableValue', [resolved]);
		},
		setVariableValue: (name: any, value: any) => {
			const resolved = resolveReference(name, 'variable');
			return callRpc('setVariable', [resolved, value]);
		},
		moveItemsBetweenContainers: (sourceRef: any, destinationRef: any) => {
			const source = resolveReference(sourceRef, 'object');
			const destination = resolveReference(destinationRef, 'object');
			return runCommand('CT_ITEMS_MOVE_CONTAINER_TO_CONTAINER', { param2: source, param3: destination });
		},
		setBackgroundMusic: (filename: string) => runCommand('CT_MM_SET_BACKGROUND_MUSIC', { param2: filename }),
		stopBackgroundMusic: () => runCommand('CT_MM_STOP_BACKGROUND_MUSIC', { param2: '' }),
		playSoundEffect: (filename: string) => runCommand('CT_MM_PLAY_SOUNDEFFECT', { param2: filename }),
		displayPicture: (filename: string) => runCommand('CT_DISPLAYPICTURE', { param2: filename }),
		displayLayeredPicture: (filename: string) => runCommand('CT_DISPLAYLAYEREDPICTURE', { param2: filename }),
		setMainCompass: (filename: string) => runCommand('CT_MM_SET_MAIN_COMPASS', { param2: filename }),
		setUpDownCompass: (filename: string) => runCommand('CT_MM_SET_UD_COMPASS', { param2: filename }),
	};

	const roomCache = new Map<string, any>();
	const objectCache = new Map<string, any>();
	const characterCache = new Map<string, any>();
	const timerCache = new Map<string, any>();
	const variableCache = new Map<string, any>();
	let playerCache: any = null;

	const mergeInto = <T extends Record<string, any>>(target: T, source: any) => {
		if (source && typeof source === 'object') {
			Object.assign(target, source);
		}
		return target;
	};

	const readCustomPropertyValue = (target: any, propertyName: string): string => {
		if (!target || !propertyName) return '';
		const props = target.CustomProperties || target.customProperties || [];
		if (!Array.isArray(props)) return '';
		const found = props.find((prop: any) => prop?.Name === propertyName || prop?.name === propertyName);
		if (!found) return '';
		return String(found.Value ?? found.value ?? '');
	};

	const resolveCustomPropertyArgs = (opOrValue: any, maybeValue?: any) => {
		let operator = 'Equals';
		let value = opOrValue;
		if (maybeValue !== undefined) {
			operator = String(opOrValue || 'Equals');
			value = maybeValue;
		}
		return { operator, value };
	};

	const readFirstCustomPropertyValue = (target: any, propertyNames: string[]): string => {
		if (!target || !Array.isArray(propertyNames)) return '';
		for (const name of propertyNames) {
			const value = readCustomPropertyValue(target, name);
			if (value) return value;
		}
		return '';
	};

	const resolveImageName = (candidates: Array<string | undefined | null>): string => {
		for (const candidate of candidates) {
			if (candidate && String(candidate).trim().length > 0) {
				return String(candidate).trim();
			}
		}
		return '';
	};

	const readVariableValue = (variable: any): any => {
		if (!variable) return null;
		const type = String(variable.vartype || '').toUpperCase();
		if (type.endsWith('ARRAY')) return Array.isArray(variable.VarArray) ? variable.VarArray : [];
		if (type.includes('NUMBER')) return variable.dNumType;
		if (type.includes('DATETIME')) return variable.dtDateTime;
		return variable.sString;
	};

	const withRef = <T extends Record<string, any>>(obj: T, type: string, id: string) => {
		(obj as any).__ragsRef = { type, id };
		return obj;
	};

	const defineAlias = (target: any, alias: string, source: string) => {
		if (!target || alias in target) return;
		Object.defineProperty(target, alias, {
			get: () => target[source],
			set: (value) => { target[source] = value; },
			enumerable: true,
			configurable: true,
		});
	};

	const defineReadOnly = (target: any, alias: string, value: any) => {
		if (!target || alias in target) return;
		Object.defineProperty(target, alias, {
			get: () => value,
			enumerable: true,
			configurable: true,
		});
	};

	const defineComputed = (target: any, alias: string, getter: () => any) => {
		if (!target || alias in target) return;
		Object.defineProperty(target, alias, {
			get: getter,
			enumerable: true,
			configurable: true,
		});
	};

	const applyCommonAliases = (base: any, type: string, id: string) => {
		defineReadOnly(base, 'type', type);
		defineReadOnly(base, 'id', id);
	};

	const wrapRoom = (room: any) => {
		if (!room) return null;
		const id = room.UniqueID || room.Name || '';
		const cached = roomCache.get(id);
		if (cached) {
			return mergeInto(cached, room);
		}
		const base = withRef({ ...room }, 'room', id);
		applyCommonAliases(base, 'room', id);
		defineAlias(base, 'name', 'Name');
		defineAlias(base, 'displayName', 'SDesc');
		defineAlias(base, 'description', 'Description');
		defineAlias(base, 'group', 'Group');
		defineAlias(base, 'picture', 'RoomPic');
		defineAlias(base, 'layeredPicture', 'LayeredRoomPic');
		defineAlias(base, 'enterFirstTime', 'bEnterFirstTime');
		defineAlias(base, 'leaveFirstTime', 'bLeaveFirstTime');
		defineAlias(base, 'exits', 'Exits');
		defineAlias(base, 'customProperties', 'CustomProperties');
		const wrapper = Object.assign(base, {
			refresh: async () => {
				const fresh = await callRpc('getRoom', [id]);
				return mergeInto(base, fresh);
			},
			getCustomProperty: (propertyName: string) => readCustomPropertyValue(base, propertyName),
			setCustomProperty: (propertyName: string, opOrValue: any, maybeValue?: any) => {
				const { operator, value } = resolveCustomPropertyArgs(opOrValue, maybeValue);
				return runCommand('CT_ROOM_SET_CUSTOM_PROPERTY', { param2: `${id}:${propertyName}`, param3: operator, param4: value ?? '' });
			},
			setCustomPropertyWithScript: (propertyName: string, opOrScript: any, maybeScript?: any) => {
				const { operator, value } = resolveCustomPropertyArgs(opOrScript, maybeScript);
				const script = value;
				return runCommand('CT_ROOM_SET_CUSTOM_PROPERTY_JS', { param2: `${id}:${propertyName}`, param3: operator, param4: script || '' });
			},
			setDescription: (text: string) => runCommand('CT_SETROOMDESCRIPTION', { commandName: text, param2: id }),
			setName: (name: string) => runCommand('CT_ROOM_SET_NAME_OVERRIDE', { param2: id, param4: name }),
			displayDescription: () => runCommand('CT_DISPLAYROOMDESCRIPTION', { param2: id }),
			displayPicture: () => runCommand('CT_DISPLAYROOMPICTURE', { param2: id }),
			setPicture: (filename: string) => runCommand('CT_SETROOMPIC', { param2: id, param3: filename }),
			setLayeredPicture: (filename: string) => runCommand('CT_SETROOMLAYEREDPIC', { param2: id, param3: filename }),
			setActionActive: (actionName: string, active: boolean) => runCommand('CT_SETROOMACTION', { param2: id, param3: `${actionName}-${active ? 'Active' : 'Inactive'}` }),
			setExitActive: (direction: string, active: boolean) => runCommand('CT_SETEXIT', { param2: id, param3: `${direction}-${active ? 'Active' : 'Inactive'}` }),
			setExitDestination: (direction: string, destRoom: any) => runCommand('CT_SETEXITDESTINATION', { param2: id, param3: direction, param4: resolveReference(destRoom, 'room') }),
			moveItemsToPlayer: () => runCommand('CT_ROOM_MOVE_ITEMS_TO_PLAYER', { param2: id }),
		});
		roomCache.set(id, wrapper);
		return wrapper;
	};

	const wrapObject = (obj: any) => {
		if (!obj) return null;
		const id = obj.UniqueIdentifier || obj.name || '';
		const cached = objectCache.get(id);
		if (cached) {
			return mergeInto(cached, obj);
		}
		const base = withRef({ ...obj }, 'object', id);
		applyCommonAliases(base, 'object', id);
		defineAlias(base, 'displayName', 'sdesc');
		defineAlias(base, 'group', 'GroupName');
		defineAlias(base, 'description', 'description');
		defineAlias(base, 'carryable', 'bCarryable');
		defineAlias(base, 'wearable', 'bWearable');
		defineAlias(base, 'openable', 'bOpenable');
		defineAlias(base, 'lockable', 'bLockable');
		defineAlias(base, 'enterable', 'bEnterable');
		defineAlias(base, 'readable', 'bReadable');
		defineAlias(base, 'container', 'bContainer');
		defineAlias(base, 'weight', 'dWeight');
		defineAlias(base, 'worn', 'bWorn');
		defineAlias(base, 'read', 'bRead');
		defineAlias(base, 'locked', 'bLocked');
		defineAlias(base, 'open', 'bOpen');
		defineAlias(base, 'entered', 'bEntered');
		defineAlias(base, 'visible', 'bVisible');
		defineAlias(base, 'enterFirstTime', 'bEnterFirstTime');
		defineAlias(base, 'leaveFirstTime', 'bLeaveFirstTime');
		defineAlias(base, 'important', 'bImportant');
		defineAlias(base, 'layeredZoneLevels', 'LayeredZoneLevels');
		defineAlias(base, 'customProperties', 'CustomProperties');
		defineComputed(base, 'location', () => {
			const locType = base.locationtype;
			const locName = base.locationname;
			if (!locType || locType === 'LT_NULL') return null;
			if (locType === 'LT_ROOM') return wrapRoom({ UniqueID: locName, Name: locName });
			if (locType === 'LT_PLAYER') return wrapPlayer({ Name: base.name || '' });
			if (locType === 'LT_IN_OBJECT') return wrapObject({ UniqueIdentifier: locName, name: locName });
			if (locType === 'LT_CHARACTER') return wrapCharacter({ Charname: locName });
			return null;
		});
		const wrapper = Object.assign(base, {
			refresh: async () => {
				const fresh = await callRpc('getObject', [id]);
				return mergeInto(base, fresh);
			},
			getCustomProperty: (propertyName: string) => readCustomPropertyValue(base, propertyName),
			setCustomProperty: (propertyName: string, opOrValue: any, maybeValue?: any) => {
				const { operator, value } = resolveCustomPropertyArgs(opOrValue, maybeValue);
				return runCommand('CT_ITEM_SET_CUSTOM_PROPERTY', { param2: `${id}:${propertyName}`, param3: operator, param4: value ?? '' });
			},
			setCustomPropertyWithScript: (propertyName: string, opOrScript: any, maybeScript?: any) => {
				const { operator, value } = resolveCustomPropertyArgs(opOrScript, maybeScript);
				const script = value;
				return runCommand('CT_ITEM_SET_CUSTOM_PROPERTY_JS', { param2: `${id}:${propertyName}`, param3: operator, param4: script || '' });
			},
			setName: (name: string) => runCommand('CT_ITEM_SET_NAME_OVERRIDE', { param2: id, param4: name }),
			setDescription: (text: string) => runCommand('CT_SETITEMDESC', { commandName: text, param2: id }),
			displayDescription: () => runCommand('CT_DISPLAYITEMDESC', { param2: id }),
			displayPicture: () => {
				const imageName = resolveImageName([
					(base as any).ObjectPic,
					(base as any).ItemPic,
					(base as any).ObjPic,
					(base as any).Pic,
					(base as any).Picture,
					(base as any).Image,
					(base as any).Media,
					(base as any).Icon,
					readFirstCustomPropertyValue(base, ['Picture', 'Image', 'Media', 'Icon', 'Pic']),
				]);
				if (!imageName) return;
				return runCommand('CT_DISPLAYPICTURE', { param2: imageName });
			},
			setVisible: (visible: boolean) => runCommand('CT_ITEM_SET_VISIBILITY', { param2: id, param3: visible ? 'Visible' : 'Invisible' }),
			setActionActive: (actionName: string, active: boolean) => runCommand('CT_SETOBJECTACTION', { param2: id, param3: `${actionName}-${active ? 'Active' : 'Inactive'}` }),
			setLocked: (locked: boolean) => runCommand('CT_SETLOCKEDUNLOCKED', { param2: id, param3: locked ? 'Locked' : 'Unlocked' }),
			setOpen: (open: boolean) => runCommand('CT_SETOPENCLOSED', { param2: id, param3: open ? 'Open' : 'Closed' }),
			setWorn: (worn: boolean) => runCommand('CT_SETITEMTOWORN', { param2: id, param3: worn ? 'Worn' : 'Removed' }),
			moveToRoom: (roomRef: any) => runCommand('CT_MOVEITEMTOROOM', { param2: id, param3: resolveReference(roomRef, 'room') }),
			moveToInventory: () => runCommand('CT_MOVEITEMTOINV', { param2: id }),
			moveToCharacter: (charRef: any) => runCommand('CT_MOVEITEMTOCHAR', { param2: id, param3: resolveReference(charRef, 'character') }),
			moveToObject: (objRef: any) => runCommand('CT_MOVEITEMTOOBJ', { param2: id, param3: resolveReference(objRef, 'object') }),
		});
		objectCache.set(id, wrapper);
		return wrapper;
	};

	const wrapCharacter = (char: any) => {
		if (!char) return null;
		const id = char.Charname || char.Name || '';
		const cached = characterCache.get(id);
		if (cached) {
			return mergeInto(cached, char);
		}
		const base = withRef({ ...char }, 'character', id);
		applyCommonAliases(base, 'character', id);
		defineAlias(base, 'name', 'Charname');
		defineAlias(base, 'displayName', 'CharnameOverride');
		defineAlias(base, 'gender', 'CharGender');
		defineAlias(base, 'description', 'Description');
		defineAlias(base, 'portrait', 'CharPortrait');
		defineAlias(base, 'currentRoomId', 'CurrentRoom');
		defineAlias(base, 'allowInventoryInteraction', 'bAllowInventoryInteraction');
		defineAlias(base, 'enterFirstTime', 'bEnterFirstTime');
		defineAlias(base, 'leaveFirstTime', 'bLeaveFirstTime');
		defineAlias(base, 'customProperties', 'CustomProperties');
		defineComputed(base, 'currentRoom', () => {
			const roomId = base.CurrentRoom;
			return roomId ? wrapRoom({ UniqueID: roomId, Name: roomId }) : null;
		});
		const wrapper = Object.assign(base, {
			refresh: async () => {
				const fresh = await callRpc('getCharacter', [id]);
				return mergeInto(base, fresh);
			},
			getCustomProperty: (propertyName: string) => readCustomPropertyValue(base, propertyName),
			setCustomProperty: (propertyName: string, opOrValue: any, maybeValue?: any) => {
				const { operator, value } = resolveCustomPropertyArgs(opOrValue, maybeValue);
				return runCommand('CT_CHAR_SET_CUSTOM_PROPERTY', { param2: `${id}:${propertyName}`, param3: operator, param4: value ?? '' });
			},
			setCustomPropertyWithScript: (propertyName: string, opOrScript: any, maybeScript?: any) => {
				const { operator, value } = resolveCustomPropertyArgs(opOrScript, maybeScript);
				const script = value;
				return runCommand('CT_CHAR_SET_CUSTOM_PROPERTY_JS', { param2: `${id}:${propertyName}`, param3: operator, param4: script || '' });
			},
			setName: (name: string) => runCommand('CT_CHAR_SET_NAME', { param2: id, param4: name }),
			setGender: (gender: string) => runCommand('CT_CHAR_SET_GENDER', { param2: id, param3: gender }),
			setDescription: (text: string) => runCommand('CT_SETCHARDESC', { commandName: text, param2: id }),
			displayDescription: () => runCommand('CT_DISPLAYCHARDESC', { param2: id }),
			displayPicture: () => {
				const imageName = resolveImageName([
					(base as any).CharPortrait,
					(base as any).portrait,
					readFirstCustomPropertyValue(base, ['Portrait', 'Picture', 'Image', 'Media']),
				]);
				if (!imageName) return;
				return runCommand('CT_DISPLAYPICTURE', { param2: imageName });
			},
			displayPortrait: () => runCommand('CT_CHAR_DISPLAYPORT', { param2: id }),
			setPortrait: (filename: string) => runCommand('CT_CHAR_SETPORT', { param2: id, param3: filename }),
			setActionActive: (actionName: string, active: boolean) => runCommand('CT_SETCHARACTION', { param2: id, param3: `${actionName}-${active ? 'Active' : 'Inactive'}` }),
			moveToRoom: (roomRef: any) => runCommand('CT_MOVECHAR', { param2: id, param3: resolveReference(roomRef, 'room') }),
			moveToObject: (objRef: any) => runCommand('CT_CHAR_MOVETOOBJ', { param2: id, param3: resolveReference(objRef, 'object') }),
			moveInventoryToPlayer: () => runCommand('CT_MOVECHARINVTOPLAYER', { param2: id }),
		});
		characterCache.set(id, wrapper);
		return wrapper;
	};

	const wrapPlayer = (player: any) => {
		if (!player) return null;
		if (playerCache) {
			return mergeInto(playerCache, player);
		}
		const base = withRef({ ...player }, 'player', 'player');
		applyCommonAliases(base, 'player', 'player');
		defineAlias(base, 'name', 'Name');
		defineAlias(base, 'gender', 'PlayerGender');
		defineAlias(base, 'description', 'Description');
		defineAlias(base, 'portrait', 'PlayerPortrait');
		defineAlias(base, 'layeredPortrait', 'PlayerLayeredImage');
		defineAlias(base, 'currentRoomId', 'CurrentRoom');
		defineAlias(base, 'promptForName', 'bPromptForName');
		defineAlias(base, 'promptForGender', 'bPromptForGender');
		defineAlias(base, 'enforceWeight', 'bEnforceWeight');
		defineAlias(base, 'weightLimit', 'dWeightLimit');
		defineAlias(base, 'customProperties', 'CustomProperties');
		defineComputed(base, 'currentRoom', () => {
			const roomId = base.CurrentRoom;
			return roomId ? wrapRoom({ UniqueID: roomId, Name: roomId }) : null;
		});
		const wrapper = Object.assign(base, {
			refresh: async () => {
				const fresh = await callRpc('getPlayer', []);
				return mergeInto(base, fresh);
			},
			getCustomProperty: (propertyName: string) => readCustomPropertyValue(base, propertyName),
			setCustomProperty: (propertyName: string, opOrValue: any, maybeValue?: any) => {
				const { operator, value } = resolveCustomPropertyArgs(opOrValue, maybeValue);
				return runCommand('CT_PLAYER_SET_CUSTOM_PROPERTY', { param2: propertyName, param3: operator, param4: value ?? '' });
			},
			setCustomPropertyWithScript: (propertyName: string, opOrScript: any, maybeScript?: any) => {
				const { operator, value } = resolveCustomPropertyArgs(opOrScript, maybeScript);
				const script = value;
				return runCommand('CT_PLAYER_SET_CUSTOM_PROPERTY_JS', { param2: propertyName, param3: operator, param4: script || '' });
			},
			setName: (name: string) => runCommand('CT_SETPLAYERNAME', { param4: name }),
			setGender: (gender: string) => runCommand('CT_SETPLAYERGENDER', { param2: gender }),
			setDescription: (text: string) => runCommand('CT_SETPLAYERDESC', { commandName: text }),
			displayDescription: () => runCommand('CT_DISPLAYPLAYERDESC', {}),
			displayPicture: () => {
				const imageName = resolveImageName([
					(base as any).PlayerPortrait,
					(base as any).portrait,
					readFirstCustomPropertyValue(base, ['Portrait', 'Picture', 'Image', 'Media']),
				]);
				if (!imageName) return;
				return runCommand('CT_DISPLAYPICTURE', { param2: imageName });
			},
			setPortrait: (filename: string) => runCommand('CT_SETPLAYERPORTRAIT', { param2: filename }),
			setLayeredPortrait: (filename: string) => runCommand('CT_SETLAYEREDPLAYERPORTRAIT', { param2: filename }),
			setActionActive: (actionName: string, active: boolean) => runCommand('CT_SETPLAYERACTION', { param3: `${actionName}-${active ? 'Active' : 'Inactive'}` }),
			moveToRoom: (roomRef: any) => runCommand('CT_MOVEPLAYER', { param2: resolveReference(roomRef, 'room') }),
			moveToCharacter: (charRef: any) => runCommand('CT_MOVETOCHAR', { param2: resolveReference(charRef, 'character') }),
			moveToObject: (objRef: any) => runCommand('CT_MOVETOOBJ', { param2: resolveReference(objRef, 'object') }),
			moveInventoryToRoom: (roomRef: any) => runCommand('CT_MOVEINVENTORYTOROOM', { param2: resolveReference(roomRef, 'room') }),
			moveInventoryToCharacter: (charRef: any) => runCommand('CT_MOVEINVENTORYTOCHAR', { param2: resolveReference(charRef, 'character') }),
		});
		playerCache = wrapper;
		return wrapper;
	};

	const wrapTimer = (timer: any) => {
		if (!timer) return null;
		const id = timer.Name || '';
		const cached = timerCache.get(id);
		if (cached) {
			return mergeInto(cached, timer);
		}
		const base = withRef({ ...timer }, 'timer', id);
		applyCommonAliases(base, 'timer', id);
		defineAlias(base, 'name', 'Name');
		defineAlias(base, 'timerType', 'TType');
		defineAlias(base, 'active', 'Active');
		defineAlias(base, 'restart', 'Restart');
		defineAlias(base, 'turnNumber', 'TurnNumber');
		defineAlias(base, 'length', 'Length');
		defineAlias(base, 'liveTimer', 'LiveTimer');
		defineAlias(base, 'seconds', 'TimerSeconds');
		defineAlias(base, 'currentTickCount', 'curtickcount');
		defineAlias(base, 'customProperties', 'CustomProperties');
		const wrapper = Object.assign(base, {
			refresh: async () => {
				const fresh = await callRpc('getTimer', [id]);
				return mergeInto(base, fresh);
			},
			getCustomProperty: (propertyName: string) => readCustomPropertyValue(base, propertyName),
			setCustomProperty: (propertyName: string, opOrValue: any, maybeValue?: any) => {
				const { operator, value } = resolveCustomPropertyArgs(opOrValue, maybeValue);
				return runCommand('CT_TIMER_SET_CUSTOM_PROPERTY', { param2: `${id}:${propertyName}`, param3: operator, param4: value ?? '' });
			},
			setCustomPropertyWithScript: (propertyName: string, opOrScript: any, maybeScript?: any) => {
				const { operator, value } = resolveCustomPropertyArgs(opOrScript, maybeScript);
				const script = value;
				return runCommand('CT_TIMER_SET_CUSTOM_PROPERTY_JS', { param2: `${id}:${propertyName}`, param3: operator, param4: script || '' });
			},
			setActive: (active: boolean) => runCommand('CT_SETTIMER', { param2: id, param3: active ? 'Active' : 'Inactive' }),
			reset: () => runCommand('CT_RESETTIMER', { param2: id }),
			execute: () => runCommand('CT_EXECUTETIMER', { param2: id }),
		});
		timerCache.set(id, wrapper);
		return wrapper;
	};

	const wrapVariable = (variable: any) => {
		if (!variable) return null;
		const id = variable.varname || variable.Name || '';
		const cached = variableCache.get(id);
		if (cached) {
			return mergeInto(cached, variable);
		}
		const base = withRef({ ...variable }, 'variable', id);
		applyCommonAliases(base, 'variable', id);
		defineAlias(base, 'name', 'varname');
		defineAlias(base, 'group', 'GroupName');
		defineAlias(base, 'valueType', 'vartype');
		defineAlias(base, 'numberValue', 'dNumType');
		defineAlias(base, 'stringValue', 'sString');
		defineAlias(base, 'dateTimeValue', 'dtDateTime');
		defineAlias(base, 'arrayValue', 'VarArray');
		defineAlias(base, 'min', 'dMin');
		defineAlias(base, 'max', 'dMax');
		defineAlias(base, 'enforceRestrictions', 'bEnforceRestrictions');
		defineAlias(base, 'comment', 'VarComment');
		defineAlias(base, 'customProperties', 'CustomProperties');
		defineComputed(base, 'value', () => readVariableValue(base));
		const wrapper = Object.assign(base, {
			refresh: async () => {
				const fresh = await callRpc('getVariable', [id]);
				return mergeInto(base, fresh);
			},
			getCustomProperty: (propertyName: string) => readCustomPropertyValue(base, propertyName),
			setCustomProperty: (propertyName: string, opOrValue: any, maybeValue?: any) => {
				const { operator, value } = resolveCustomPropertyArgs(opOrValue, maybeValue);
				return runCommand('CT_VARIABLE_SET_CUSTOM_PROPERTY', { param2: `${id}:${propertyName}`, param3: operator, param4: value ?? '' });
			},
			setCustomPropertyWithScript: (propertyName: string, opOrScript: any, maybeScript?: any) => {
				const { operator, value } = resolveCustomPropertyArgs(opOrScript, maybeScript);
				const script = value;
				return runCommand('CT_VARIABLE_SET_CUSTOM_PROPERTY_JS', { param2: `${id}:${propertyName}`, param3: operator, param4: script || '' });
			},
		});
		variableCache.set(id, wrapper);
		return wrapper;
	};

	const wrapObjectMaybe = (obj: any, type: string) => {
		if (type === 'room') return wrapRoom(obj);
		if (type === 'object') return wrapObject(obj);
		if (type === 'character') return wrapCharacter(obj);
		if (type === 'timer') return wrapTimer(obj);
		if (type === 'variable') return wrapVariable(obj);
		if (type === 'player') return wrapPlayer(obj);
		return obj;
	};

	const wrapSelf = (payload: any) => {
		if (!payload) return null;
		const inferredType = detectSelfTypeLocal(payload);
		const type = payload.__selfType || payload.type || inferredType || '';
		const value = payload.value ?? payload;
		if (type === 'room') return wrapRoom(value);
		if (type === 'object') return wrapObject(value);
		if (type === 'character') return wrapCharacter(value);
		if (type === 'timer') return wrapTimer(value);
		if (type === 'player') return wrapPlayer(value);
		return value;
	};

	const wrapInputValue = (value: any) => {
		if (!value || typeof value !== 'object') return value;
		if ((value as any).__ragsRef) return value;
		if ((value as any).UniqueIdentifier || (value as any).locationtype) return wrapObject(value);
		if ((value as any).Charname || (value as any).CharGender) return wrapCharacter(value);
		if ((value as any).UniqueID || (value as any).RoomPic) return wrapRoom(value);
		return value;
	};

	for (const spec of specs) {
		globals[spec.functionName] = (payload: any) => runCommand(spec.commandType, buildCommandPayload(spec, payload, textCommandTypes));
	}

	const wrapRpc = async (method: string, args: any[], type: string) => {
		const result = await callRpc(method, args);
		return wrapObjectMaybe(result, type);
	};

	globals.getLastInput = async () => wrapInputValue(await callRpc('getAdditionalData', []));
	globals.requestInput = async (options?: any) => wrapInputValue(await callRpc('requestInput', [options || {}]));
	globals.getRoomById = async (id: string) => wrapRoom(await callRpc('getRoom', [id]));
	globals.getRoomByName = async (name: string) => wrapRoom(await callRpc('getRoom', [name]));
	globals.getObjectById = async (id: string) => wrapObject(await callRpc('getObject', [id]));
	globals.getObjectByName = async (name: string) => wrapObject(await callRpc('getObject', [name]));
	globals.getCharacterByName = async (name: string) => wrapCharacter(await callRpc('getCharacter', [name]));
	globals.getTimerByName = async (name: string) => wrapTimer(await callRpc('getTimer', [name]));
	globals.getVariableByName = async (name: string) => wrapVariable(await callRpc('getVariable', [name]));
	globals.getPlayer = async () => wrapPlayer(await callRpc('getPlayer', []));
	globals.getCurrentRoom = async () => wrapRoom(await callRpc('getCurrentRoom', []));
	globals.getCurrentObject = async () => wrapObject(await callRpc('getCurrentObject', []));
	globals.getSelf = async () => wrapSelf(await callRpc('getSelf', []));
	globals.getAllItems = async () => {
		const items = await callRpc('getAllObjects', []);
		return Array.isArray(items) ? items.map((item: any) => wrapObject(item)) : [];
	};
	globals.getAllRooms = async () => {
		const rooms = await callRpc('getAllRooms', []);
		return Array.isArray(rooms) ? rooms.map((room: any) => wrapRoom(room)) : [];
	};
	globals.getAllCharacters = async () => {
		const chars = await callRpc('getAllCharacters', []);
		return Array.isArray(chars) ? chars.map((char: any) => wrapCharacter(char)) : [];
	};
	globals.getItemsInRoom = async (roomRef: any) => {
		const roomId = resolveReference(roomRef, 'room');
		const items = await callRpc('getItemsInRoom', [roomId]);
		return Array.isArray(items) ? items.map((item: any) => wrapObject(item)) : [];
	};
	globals.getItemsInObject = async (objectRef: any) => {
		const objectId = resolveReference(objectRef, 'object');
		const items = await callRpc('getItemsInObject', [objectId]);
		return Array.isArray(items) ? items.map((item: any) => wrapObject(item)) : [];
	};
	globals.getItemsInInventory = async () => {
		const items = await callRpc('getItemsInInventory', []);
		return Array.isArray(items) ? items.map((item: any) => wrapObject(item)) : [];
	};
	globals.getItemsInCharacterInventory = async (characterRef: any) => {
		const characterId = resolveReference(characterRef, 'character');
		const items = await callRpc('getItemsInCharacterInventory', [characterId]);
		return Array.isArray(items) ? items.map((item: any) => wrapObject(item)) : [];
	};
	globals.getItemsInGroup = async (groupName: string) => {
		const items = await callRpc('getItemsInGroup', [groupName || '']);
		return Array.isArray(items) ? items.map((item: any) => wrapObject(item)) : [];
	};
	globals.getRoomsInGroup = async (groupName: string) => {
		const rooms = await callRpc('getRoomsInGroup', [groupName || '']);
		return Array.isArray(rooms) ? rooms.map((room: any) => wrapRoom(room)) : [];
	};
	globals.getCharactersInGroup = async (groupName: string) => {
		const chars = await callRpc('getCharactersInGroup', [groupName || '']);
		return Array.isArray(chars) ? chars.map((char: any) => wrapCharacter(char)) : [];
	};
	globals.getVariablesInGroup = async (groupName: string) => {
		const variables = await callRpc('getVariablesInGroup', [groupName || '']);
		return Array.isArray(variables) ? variables.map((variable: any) => wrapVariable(variable)) : [];
	};
	globals.getTimersInGroup = async (groupName: string) => {
		const timers = await callRpc('getTimersInGroup', [groupName || '']);
		return Array.isArray(timers) ? timers.map((timer: any) => wrapTimer(timer)) : [];
	};
	globals.getRoomExits = async (roomRef: any) => {
		const roomId = resolveReference(roomRef, 'room');
		const exits = await callRpc('getRoomExits', [roomId]);
		return Array.isArray(exits) ? exits : [];
	};

	return { globals };
}

const buildAdvancedApiSource = () => {
	return `const createAdvancedApi = ${createAdvancedApi.toString()};`;
};

const ADVANCED_API_SOURCE = buildAdvancedApiSource();

const ADVANCED_API_SPEC = {
	specs: COMMAND_SPECS,
	textCommandTypes: Array.from(COMMAND_NAME_TEXT_TYPES),
	constants: {
		roomReferenceTypes: Array.from(ROOM_REFERENCE_TYPES),
		objectReferenceTypes: Array.from(OBJECT_REFERENCE_TYPES),
		currentRoomGuid: CurrentRoomGuid,
		voidRoomGuid: VoidRoomGuid,
		selfObjectGuid: SelfObjectGuid,
	},
};
/**
 * Set borders on room thumb and player image based on available actions
 */
export function SetBorders(): void {
	if (!TheGame) return;
	const currentRoom = Finder.room(TheGame.Player.CurrentRoom);
	if (currentRoom && GetActionCount(currentRoom.Actions) > 0) {
		$dom('#RoomThumbImg').addClass('has-actions');
	} else {
		$dom('#RoomThumbImg').removeClass('has-actions');
	}
	if (GetActionCount(TheGame.Player.Actions) > 0) {
		$dom('#PlayerImg').addClass('has-actions');
	} else {
		$dom('#PlayerImg').removeClass('has-actions');
	}
}

/**
 * Get count of visible actions
 */
export function GetActionCount(Actions: Action[]): number {
	let count = 0;
	for (let i = 0; i < Actions.length; i++) {
		if (actionShouldBeVisible(Actions[i])) {
			count++;
		}
	}
	return count;
}

/**
 * Image path cache with LRU management for blob URLs
 */
const imagePaths: { [key: string]: string } = {};
const imagePathPromises: { [key: string]: Promise<string> } = {};

// LRU cache for blob URLs - tracks last access time
interface BlobURLEntry {
	url: string;
	lastAccessed: number;
}

const blobURLCache = new Map<string, BlobURLEntry>();
const MAX_BLOB_URLS = 100; // Maximum number of active blob URLs

/**
 * Clean up least recently used blob URLs when cache is full
 */
function cleanupBlobURLCache(): void {
	if (blobURLCache.size < MAX_BLOB_URLS) {
		return;
	}

	// Sort by last accessed time (oldest first)
	const entries = Array.from(blobURLCache.entries())
		.sort((a, b) => a[1].lastAccessed - b[1].lastAccessed);

	// Remove oldest 20% of entries
	const toRemove = Math.floor(entries.length * 0.2);
	for (let i = 0; i < toRemove; i++) {
		const [imageName, entry] = entries[i];
		URL.revokeObjectURL(entry.url);
		blobURLCache.delete(imageName);
		delete imagePaths[imageName];
	}
}

/**
 * Get or create blob URL with LRU management
 */
function getOrCreateBlobURL(imageName: string, blob: Blob): string {
	const now = Date.now();
	
	// Check if we already have a URL for this image
	if (blobURLCache.has(imageName)) {
		const entry = blobURLCache.get(imageName)!;
		entry.lastAccessed = now;
		return entry.url;
	}

	// Create new blob URL
	const url = URL.createObjectURL(blob);
	blobURLCache.set(imageName, {
		url: url,
		lastAccessed: now
	});

	// Clean up if needed
	cleanupBlobURLCache();

	return url;
}

/**
 * Get image path for an image name (async version)
 */
export async function imagePathAsync(imageName: string): Promise<string> {
	if (imagePaths[imageName]) {
		// Update last accessed time if it's a blob URL
		if (blobURLCache.has(imageName)) {
			blobURLCache.get(imageName)!.lastAccessed = Date.now();
		}
		return imagePaths[imageName];
	}

	// If already loading, return the existing promise
	if (imageName in imagePathPromises) {
		return imagePathPromises[imageName];
	}

	const promise = (async () => {
		const gameImage = (window as any).GetGameImage?.(imageName);
		if (!gameImage) {
			const normalized = imageName?.replace(/\\/g, '/');
			if (normalized && normalized.includes('/')) {
				imagePaths[imageName] = 'images/' + normalized;
				delete imagePathPromises[imageName];
				return imagePaths[imageName];
			}
			console.log('Unable to find any image named \'' + imageName + '\'');
			return '';
		}

		const path = gameImage.TheName.replace(/'/g, '\\\'');
		imagePaths[imageName] = 'images/' + path;

		delete imagePathPromises[imageName];
		return imagePaths[imageName];
	})();

	imagePathPromises[imageName] = promise;
	return promise;
}

/**
 * Get image path for an image name (sync version, checks cache only)
 */
export function imagePath(imageName: string): string {
	if (imagePaths[imageName]) {
		// Update last accessed time if it's a blob URL
		if (blobURLCache.has(imageName)) {
			blobURLCache.get(imageName)!.lastAccessed = Date.now();
		}
		return imagePaths[imageName];
	}

	const gameImage = (window as any).GetGameImage?.(imageName);
	if (!gameImage) {
		const normalized = imageName?.replace(/\\/g, '/');
		if (normalized && normalized.includes('/')) {
			imagePaths[imageName] = 'images/' + normalized;
			return imagePaths[imageName];
		}
		console.log('Unable to find any image named \'' + imageName + '\'');
		return '';
	}

	const path = gameImage.TheName.replace(/'/g, '\\\'');
	imagePaths[imageName] = 'images/' + path;

	return imagePaths[imageName];
}

/**
 * Get CSS url for image
 */
export function imageUrl(imageName: string): string {
	return 'url(\'' + imagePath(imageName) + '\')';
}

/**
 * Set room thumbnail image
 */
export function SetRoomThumb(ImageName: string | null): void {
	if (ImageName == null || ImageName == 'None')
		return;

	$dom('#RoomImageLayers').empty();

	$dom('#RoomThumbImg').css('background-image', imageUrl(ImageName));

	const checkimg = (window as any).GetGameImage?.(ImageName);
	const thelayers = checkimg != null ? getLayeredImagesList(checkimg) : [];
	const roomLayer = currentRoomLayeredPic();
	if (roomLayer) thelayers.push(roomLayer);
	for (let i = 0; i < thelayers.length; i++) {
		const img = $dom(document.createElement('div')).addClass('RoomLayeredImage');
		img.css('background-image', imageUrl(thelayers[i]));
		img.on('click', (clickEvent: Event) => {
			// Layers cover the thumbnail, so honour its disabled state here too.
			if ((document.getElementById('RoomThumbImg') as HTMLElement | null)?.dataset.disabled) return;
			(window as any).Globals.theObj = Finder.room(TheGame!.Player.CurrentRoom);
			GameUI.displayActions((window as any).Globals.theObj, clickEvent as any);
		});
		$dom('#RoomImageLayers').append(img.get(0)!);
	}
}

/**
 * Main image extra layers
 */
let mainImageExtraLayers: string[] = [];

/**
 * Show main image
 */
export function showImage(ImageName: string | null): void {
	if (ImageName == null || ImageName == 'None')
		return;
	(window as any).Globals.currentImage = ImageName;
	mainImageExtraLayers = [];
	renderMainImageAndLayers();
}

const supportedMediaFormats = new Set([
	'mp4', 'webm',
	'gif', 'jpg', 'jpeg', 'webp', 'png',
	'bmp', 'ico', 'svg',
]);

function getLayeredImagesList(imageInfo: any): string[] {
	const layers = imageInfo?.LayeredImages;
	if (!Array.isArray(layers)) return [];
	const list = typeof layers[0] === 'string' ? layers[0] : '';
	if (!list) return [];
	return list.split(',').filter((layer) => layer.trim().length > 0);
}

/** RAGS uses "None" / "<None>" (or empty) for "no layered picture". */
function isLayerImageName(name: string | null | undefined): name is string {
	return !!name && name !== 'None' && name !== '<None>';
}

/** Layered picture set on the current room (designer field or CT_SETROOMLAYEREDPIC). */
function currentRoomLayeredPic(): string | null {
	const room = TheGame ? Finder.room(TheGame.Player.CurrentRoom) : null;
	return room && isLayerImageName(room.LayeredRoomPic) ? room.LayeredRoomPic : null;
}

/**
 * CT_DISPLAYLAYEREDPICTURE: draw a layer over the current main picture.
 */
export function showLayeredPicture(layerName: string): void {
	mainImageExtraLayers = isLayerImageName(layerName) ? [layerName] : [];
	renderMainImageAndLayers();
}

/**
 * Redraw the room picture (thumbnail and, if it is showing, the main picture)
 * after the room's layered picture changed.
 */
export function refreshRoomLayeredPicture(): void {
	const room = TheGame ? Finder.room(TheGame.Player.CurrentRoom) : null;
	if (!room) return;
	SetRoomThumb(room.RoomPic);
	if ((window as any).Globals.currentImage === room.RoomPic) {
		renderMainImageAndLayers();
	}
}

/**
 * Render main image and layers
 */
function renderMainImageAndLayers(): void {
	$dom('#MainImageLayers').empty();

	let layers: string[] = [];
	const checkimg = (window as any).GetGameImage?.((window as any).Globals.currentImage);
	layers = layers.concat(getLayeredImagesList(checkimg));
	const roomLayer = currentRoomLayeredPic();
	const currentRoom = TheGame ? Finder.room(TheGame.Player.CurrentRoom) : null;
	if (roomLayer && currentRoom && currentRoom.RoomPic === (window as any).Globals.currentImage) {
		layers.push(roomLayer);
	}
	layers = layers.concat(mainImageExtraLayers);

	(window as any).ImageRecorder?.sawImage?.((window as any).Globals.currentImage);
	const fileParts = (window as any).Globals.currentImage.split('.');
	const fileExtension = fileParts[fileParts.length - 1].toLowerCase();
	if (!supportedMediaFormats.has(fileExtension) && (window as any).Globals.currentImage !== '') {
		// Try to load the media as an image and show an error message if that fails
		$dom('#MainVideo').empty();
		const ImageName = (window as any).Globals.currentImage;
		const img = new Image();
		img.addEventListener('load', () => {
			if (ImageName === (window as any).Globals.currentImage) {
				$dom('#MainImg').css('background-image', imageUrl((window as any).Globals.currentImage));
			}
		});
		img.addEventListener('error', () => {
			if (ImageName === (window as any).Globals.currentImage) {
				const errorDiv = document.createElement('div');
				errorDiv.className = 'bad-media';
				errorDiv.innerHTML = (window as any).Globals.currentImage + '<br/>Playing this file is not supported';
				$dom('#MainVideo').append(errorDiv);
				$dom('#MainImg').css('background-image', '');
			}
		});
		img.src = imagePath((window as any).Globals.currentImage);
	} else if (fileExtension === 'mp4' || fileExtension === 'webm') {
		const videoTag = document.createElement('video');
		videoTag.setAttribute('autoplay', '');
		videoTag.setAttribute('controls', '');
		videoTag.setAttribute('width', '100%');
		const source = document.createElement('source');
		source.setAttribute('src', imagePath((window as any).Globals.currentImage));
		source.setAttribute('type', 'video/' + fileExtension);
		videoTag.appendChild(source);
		videoTag.appendChild(document.createTextNode('Sorry, your browser doesn\'t support this video.'));

		$dom('#MainVideo').empty();
		$dom('#MainVideo').append(videoTag);
		$dom('#MainImg').css('background-image', '');
	} else {
		$dom('#MainVideo').empty();
		$dom('#MainImg').css('background-image', imageUrl((window as any).Globals.currentImage));
	}

	for (let i = 0; i < layers.length; i++) {
		(window as any).ImageRecorder?.sawImage?.(layers[i]);
		const img = $dom(document.createElement('div')).addClass('MainLayeredImage');
		img.css('background-image', imageUrl(layers[i]));
		$dom('#MainImageLayers').append(img.get(0)!);
	}
}

/**
 * Set player portrait
 */
export function SetPortrait(ImageName: string | null): void {
	const hidePortrait = !!TheGame?.HidePortrait;
	if (hidePortrait) {
		$dom('.compass-and-portrait').addClass('portrait-hidden');
		$dom('.portrait-holder').css('display', 'none');
		$dom('#PortraitImageLayers').empty().css('display', 'none').css('visibility', 'hidden');
		$dom('#PlayerImg').css('background-image', '').css('display', 'none').css('visibility', 'hidden');
		return;
	}

	$dom('.compass-and-portrait').removeClass('portrait-hidden');
	$dom('.portrait-holder').css('display', '');
	$dom('#PortraitImageLayers').css('display', '').css('visibility', 'visible');
	$dom('#PlayerImg').css('display', '').css('visibility', 'visible');
	// Layered portrait (CT_SETLAYEREDPLAYERPORTRAIT) drawn over the portrait.
	const playerLayer = TheGame && isLayerImageName(TheGame.Player.PlayerLayeredImage) ? TheGame.Player.PlayerLayeredImage : null;
	if ((ImageName == null || ImageName == '') && !playerLayer)
		return;
	$dom('#PortraitImageLayers').empty();

	$dom('#PlayerImg').css('background-image', ImageName ? imageUrl(ImageName) : '');

	const checkimg = ImageName ? (window as any).GetGameImage?.(ImageName) : null;
	const layers = checkimg != null ? getLayeredImagesList(checkimg) : [];
	if (playerLayer) layers.push(playerLayer);
	for (let i = 0; i < layers.length; i++) {
		const img = $dom(document.createElement('div')).addClass('PortraitLayeredImage');
		img.css('background-image', imageUrl(layers[i]));
		img.on('click', (clickEvent: Event) => {
			if ((document.getElementById('PlayerImg') as HTMLElement | null)?.dataset.disabled) return;
			(window as any).Globals.theObj = TheGame!.Player;
			GameUI.displayActions(TheGame!.Player, clickEvent as any);
		});
		$dom('#PortraitImageLayers').append(img.get(0)!);
	}
}

/**
 * Setup status bars
 */
export function SetupStatusBars(): void {
	if (!TheGame) return;
	const visibleItemTexts = TheGame.StatusBarItems.filter((item: any) => {
		return item.bVisible;
	}).map((item: any) => {
		return item.Text;
	});
	const statbar = (window as any).PerformTextReplacements?.(visibleItemTexts.join(' | '), null) || visibleItemTexts.join(' | ');
	$dom('#statusbartext').empty().append(statbar);
}

/**
 * Check if object contains another object
 */
export function objectContainsObject(container: RagsObject, object: RagsObject): boolean {
	return (object.locationtype === 'LT_IN_OBJECT') && (object.locationname === container.UniqueIdentifier) && (object.bVisible);
}

/**
 * Check if character has object
 */
export function characterHasObject(character: Character, object: RagsObject): boolean {
	return (object.locationtype === 'LT_CHARACTER') && (object.locationname === character.Charname) && (object.bVisible);
}

/**
 * Get display name for action
 */
export function nameForAction(action: Action): string {
	if (action.overridename) {
		return (window as any).PerformTextReplacements?.(action.overridename) || action.overridename;
	} else {
		return action.name;
	}
}

/**
 * Check if action should be visible
 */
export function actionShouldBeVisible(action: Action): boolean {
	return action.name.substring(0, 2) !== '<<' && action.bActive && action.actionparent === 'None';
}

/**
 * Check if check is a loop check
 */
export function isLoopCheck(check: Check): boolean {
	const loopCondTypes = [
		'CT_Loop_While',
		'CT_Loop_Rooms',
		'CT_Loop_Characters',
		'CT_Loop_Items',
		'CT_Loop_Exits',
		'CT_Loop_Item_Char_Inventory',
		'CT_Loop_Item_Container',
		'CT_Loop_Item_Inventory',
		'CT_Loop_Item_Room',
		'CT_Loop_Item_Group',
	];
	return loopCondTypes.indexOf(check.CondType) > -1;
}

/**
 * Run next after pause
 */
export function runNextAfterPause(runNextPhase: () => void): void {
	if (gameController.shouldRunCommands()) {
		runNextPhase();
	}
}

/**
 * Run after pause
 */
export function runAfterPause(runNextPhase: () => void, commandList?: any): void {
	if (gameController.shouldRunCommands()) {
		runNextPhase();
	}
}

/**
 * Change room
 */
export function ChangeRoom(currentRoom: Room | null, bRunTimerEvents: boolean, bRunEvents: boolean, bQuiet?: boolean): void {
	void ChangeRoomAsync(currentRoom, bRunTimerEvents, bRunEvents, bQuiet);
}

function updateRoomDisplay(currentRoom: Room, bQuiet: boolean): void {

	const roomTitle = (window as any).roomDisplayName?.(currentRoom) || currentRoom.Name || '';
	const roomTitleEl = $dom('#RoomTitle').get(0) as HTMLElement;
	if (roomTitleEl) {
		roomTitleEl.textContent = roomTitle;
		roomTitleEl.setAttribute('title', roomTitle);
	}
	// Set the current room first: the picture rendering below looks up the
	// current room's layered picture.
	TheGame!.Player.CurrentRoom = currentRoom.UniqueID;
	SetRoomThumb(currentRoom.RoomPic);
	showImage(currentRoom.RoomPic);
	if (!bQuiet && (window as any).Globals.movingDirection) {
		const mainTextEl = $dom('#MainText').get(0);
		const isEmpty = !mainTextEl || mainTextEl.innerHTML.trim() === '';
		const reuseSeparator = !isEmpty && mainTextEndsWithTurnSeparator(mainTextEl);
		let directionText = '';
		if (!isEmpty && !reuseSeparator) {
			directionText = '<hr class="turn-separator">';
		}
		directionText += (reuseSeparator ? '' : '<br>') + '<b>' + (window as any).Globals.movingDirection + '</b><br><br>';
		const processedText = processTextForOutput(directionText, isEmpty);
		$dom('#MainText').append(processedText);
	}

}

/**
 * Change room and wait until the room's enter events (and description output)
 * have finished. Callers inside an async command chain must await this so the
 * enter events run sequentially (as in RAGS) instead of interleaving with the
 * caller's remaining commands. Interleaving corrupts the shared Globals stack:
 * the first chain to finish pops the other chain's frames (e.g. the frame
 * holding variableGettingSet for a pending CT_SETVARIABLEBYINPUT).
 */
export function ChangeRoomAsync(currentRoom: Room | null, bRunTimerEvents: boolean, bRunEvents: boolean, bQuiet?: boolean): Promise<void> {
	if (bQuiet === undefined) bQuiet = false;
	if (!currentRoom) return Promise.resolve();
	updateRoomDisplay(currentRoom, bQuiet);
	return runEnterEvents(currentRoom, bRunEvents, bRunTimerEvents, bQuiet);
}

async function runEnterEvents(currentRoom: Room | null, bRunEvents: boolean, bRunTimerEvents: boolean, bQuiet: boolean): Promise<void> {
	if (!currentRoom) return;
	const desiredRoomId = currentRoom.UniqueID;

	if (bRunEvents && !currentRoom.bEnterFirstTime) {
		currentRoom.bEnterFirstTime = true;
		await (window as any).GameActions?.runEventsAsync?.('<<On Player Enter First Time>>');
	}

	// Handle situations where one of the "Enter First Time" events triggers a new ChangeRoom
	if (TheGame!.Player.CurrentRoom !== desiredRoomId) {
		return;
	}

	if (bRunEvents) {
		await (window as any).GameActions?.runEventsAsync?.('<<On Player Enter>>');
	}

	// Handle situations where one of the "Enter" events triggers a new ChangeRoom
	if (TheGame!.Player.CurrentRoom !== desiredRoomId) {
		return;
	}

	scrollMainTextToTurnStart({ defer: true });
	if (!bQuiet) {
		(window as any).AddTextToRTF?.(currentRoom.Description, 'Black', 'Regular');
		scrollMainTextToTurnStart({ defer: true });
	}
	(window as any).ActionRecorder?.roomEntered?.((window as any).roomDisplayName?.(currentRoom) || currentRoom.Name);
	if (bRunTimerEvents) {
		// Await the timer tick: fire-and-forget let the timers' actions run
		// concurrently with whatever the caller does next (e.g. StartGame's
		// <<On Game Start>>), and a finishing timer action then popped the
		// caller's Globals frames, losing pending input state.
		await (window as any).GameTimers?.runTimerEventsAsync?.();
	}
	GameUI.refreshPanelItems();
	if ($dom('#RoomThumbImg').css('visibility') != 'hidden')
		SetExits();
	SetBorders();
}

/**
 * Room change helper
 */
export function RoomChange(bRunTimerEvents: boolean, bRunEvents: boolean, bQuiet?: boolean): void {
	if (!TheGame) return;
	const currentroom = Finder.room(TheGame.Player.CurrentRoom);
	ChangeRoom(currentroom || null, bRunTimerEvents, bRunEvents, bQuiet);
}

/**
 * Set exits on compass
 */
export function SetExits(): void {
	if (!TheGame) return;
	const currentroom = Finder.room(TheGame.Player.CurrentRoom);
	$dom('.compass-direction').removeClass('active');
	if (currentroom != null) {
		for (let i = 0; i < currentroom.Exits.length; i++) {
			const direction = currentroom.Exits[i].Direction;
			if (currentroom.Exits[i].DestinationRoom != '' && currentroom.Exits[i].bActive) {
				const destRoom = Finder.room((window as any).GetDestinationRoomName?.(direction));
				$dom('.compass-direction[data-direction=' + direction + ']')
					.addClass('active')
					.text((window as any).roomDisplayName?.(destRoom) || direction);
			} else {
				$dom('.compass-direction[data-direction=' + direction + ']').text('');
			}
		}
	}
}

/**
 * Refresh picture boxes
 */
export function RefreshPictureBoxes(): void {
	if (!TheGame) return;
	showImage((window as any).Globals.currentImage);
	SetPortrait(TheGame.Player.PlayerPortrait);
	const currentRoom = Finder.room(TheGame.Player.CurrentRoom);
	if (currentRoom) {
		SetRoomThumb(currentRoom.RoomPic);
	}
}

/**
 * Move player to room
 */
export function movePlayerToRoom(roomName: string): void {
	(window as any).Globals.movingDirection = '';
	TheGame!.Player.CurrentRoom = roomName;
	if (TheGame!.Player.CurrentRoom) {
		RoomChange(false, true);
	}
}

/**
 * Move player to room (async version)
 */
export function movePlayerToRoomAsync(roomName: string): Promise<void> {
	(window as any).Globals.movingDirection = '';
	TheGame!.Player.CurrentRoom = roomName;
	if (TheGame!.Player.CurrentRoom) {
		return RoomChangeAsync(false, true);
	}
	return Promise.resolve();
}

/**
 * Room change helper (async version)
 */
export function RoomChangeAsync(bRunTimerEvents: boolean, bRunEvents: boolean, bQuiet?: boolean): Promise<void> {
	if (!TheGame) return Promise.resolve();
	const currentroom = Finder.room(TheGame.Player.CurrentRoom);
	return ChangeRoomAsync(currentroom || null, bRunTimerEvents, bRunEvents, bQuiet);
}

/**
 * Add text to RTF (Rich Text Format) display
 */
export function AddTextToRTF(
	text: string,
	clr: string,
	fontst: string,
	allowHtml = false,
	options?: { suppressParagraph?: boolean }
): void {
	const suppressParagraph = Boolean(options?.suppressParagraph);
	let origtext = '';
	text = text.replace(/\r\n/g, '\n');
	while (origtext != text) {
		origtext = text;
		text = (window as any).PerformTextReplacements?.(text, null) || text;
	}
	text = text.replace(/\r\n/g, '\n');

	if (TheGame && TheGame.RagsVersion < 3.0) { // RAGS 3.0 uses HTML tags
		if (!allowHtml) {
			text = escapeHtmlSpecialCharacters(text);

			// Unescape the <br> or <br/> tags that just got escaped
			// Also replace new line characters with <br> tags
			text = text.replace(/(&lt;\s*[/]?\s*br\s*[/]?\s*&gt;|\n)/g, '<br>');
		}
	} else {
		// TODO Remove leading "<br/>"?
	}

	let replacedtext = text;
	if (fontst == 'Regular' && clr == 'Black') {
		// [c Green]green text[/c]
		replacedtext = replacedtext.replace(/\[c\s*([^\]]+)]/gi, (match, colortype) => {
			const colorinserter = '<span style=\'color:';
			let cssColor: string;
			if (colortype.indexOf(',') > -1) {
				cssColor = 'rgb(' + colortype + ')';
			} else {
				cssColor = colortype;
			}

			if ((Settings as any).darkMode) {
				return colorinserter + GameUI.getDarkColor(cssColor) +
					';\' data-color=\'' + cssColor + '\'>';
			} else {
				return colorinserter + cssColor + ';\'>';
			}
		});

		// [f Arial,16]special font[/f]
		replacedtext = replacedtext.replace(/\[f\s*([^\]]+)]/gi, (match, fonttype) => {
			const fontdata = fonttype.split(',');
			const fontFamily = (fontdata[0] || '').trim();
			const fontSize = (fontdata[1] || '').trim();
			const sizeStyle = fontSize ? 'font-size:' + fontSize + 'px;' : '';
			return '<span style=\'font-family:' + fontFamily + ';' + sizeStyle + '\'>';
		});

		// [font=Arial,16]special font[/font]
		replacedtext = replacedtext.replace(/\[font\s*=?\s*([^\]]+)]/gi, (match, fonttype) => {
			const fontdata = fonttype.split(',');
			const fontFamily = (fontdata[0] || '').trim();
			const fontSize = (fontdata[1] || '').trim();
			const sizeStyle = fontSize ? 'font-size:' + fontSize + 'px;' : '';
			return '<span style=\'font-family:' + fontFamily + ';' + sizeStyle + '\'>';
		});

		// [b]bold text[/b]
		replacedtext = replacedtext.replace(/\[b]/gi, () => {
			return '<span style=\'font-weight:bold;\'>';
		});

		// [i]italic text[/i]
		replacedtext = replacedtext.replace(/\[i]/gi, () => {
			return '<span style=\'font-style:italic;\'>';
		});

		// [u]underlined text[/u]
		replacedtext = replacedtext.replace(/\[u]/gi, () => {
			return '<span style=\'text-decoration:underline;\'>';
		});

		// closing tags ([/u], [/b]...)
		replacedtext = replacedtext.replace(/\[\/(?:f|font|c|i|u|b)]/gi, () => {
			return '</span>';
		});

		const styleformats: [string, string][] = [];
		let tempindex: number;
		text = (window as any).MiddlesOnly?.(replacedtext) || replacedtext;
		tempindex = text.indexOf('[middle]', 0);
		while (tempindex >= 0) {
			text = text.slice(0, tempindex) + text.slice(tempindex + 8);
			const endindex = text.indexOf('[/middle]', tempindex);
			if (endindex >= 0) {
				text = text.slice(0, endindex) + text.slice(endindex + 9);
				styleformats.push([
					text.substring(tempindex, endindex),
					'<div style="text-align:center;">',
				]);
			}
			tempindex = text.indexOf('[middle]', 0);
		}
		text = (window as any).TextOnly?.(replacedtext) || replacedtext;
		for (let i = 0; i < styleformats.length; i++) {
			let startindex = text.indexOf(styleformats[i][0]);
			text = text.slice(0, startindex) + styleformats[i][1] + text.slice(startindex);
			startindex = text.indexOf(styleformats[i][0]);
			text = text.slice(0, startindex + styleformats[i][0].length) + '</div>' + text.slice(startindex + styleformats[i][0].length);
		}

		// Check if MainText is empty
		const mainTextEl = $dom('#MainText').get(0);
		const isEmpty = !mainTextEl || mainTextEl.innerHTML.trim() === '';
		if (ENGINE_LOGGING_ENABLED) {
			console.log('[AddTextToRTF] Appending text (RAGS < 3.0), isEmpty:', isEmpty);
		}

		// Process text and append to MainText
		const processedText = processTextForOutput(text, isEmpty);
		if (suppressParagraph) {
			$dom('#MainText').append(processedText);
		} else {
			const paragraphDiv = document.createElement('div');
			paragraphDiv.className = 'maintext-paragraph';
			paragraphDiv.innerHTML = processedText;
			$dom('#MainText').append(paragraphDiv);
		}

		scrollMainTextToTurnStart({ defer: true });
	} else {
		// Check if MainText is empty
		const mainTextEl = $dom('#MainText').get(0);
		const isEmpty = !mainTextEl || mainTextEl.innerHTML.trim() === '';
		if (ENGINE_LOGGING_ENABLED) {
			console.log('[AddTextToRTF] Appending text (RAGS >= 3.0), isEmpty:', isEmpty);
		}

		// Process text and append to MainText
		const processedText = processTextForOutput(text, isEmpty);
		if (suppressParagraph) {
			$dom('#MainText').append(processedText);
		} else {
			const paragraphDiv = document.createElement('div');
			paragraphDiv.className = 'maintext-paragraph';
			paragraphDiv.innerHTML = processedText;
			$dom('#MainText').append(paragraphDiv);
		}

		scrollMainTextToTurnStart({ defer: true });
	}
}

/**
 * Process HTML text before appending to MainText:
 * - Only remove <br> after <hr> (keep all other <br> tags)
 * - Convert 20+ dashes to <hr> if standalone
 * - Remove leading <br> only if first append
 */
export function processTextForOutput(text: string, isFirstAppend: boolean = false): string {
	if (ENGINE_LOGGING_ENABLED) {
		console.log('[processTextForOutput] Called with:', {
			text: text/*.substring(0, 100) + (text.length > 100 ? '...' : '')*/,
			isFirstAppend,
			textLength: text.length,
		});
	}

	// Fix malformed </br> tags to proper <br> tags
	let processed = text.replace(/<\/br>/gi, '<br>');

	// Also fix <br/> to <br> for consistency
	processed = processed.replace(/<br\s*\/>/gi, '<br>');

	// Convert sequences of 20+ dashes to <hr> if standalone
	// Simple algorithm: line contains ONLY dashes (20+), surrounded by <br> or boundaries
	// Pattern: <br>dashes<br> or <br>dashes$ or ^dashes<br> or ^dashes$
	processed = processed.replace(/(<br\s*\/?>|^)(-{20,})(<br\s*\/?>|$)/gi, (match, beforeBr, dashes, afterBr, offset, fullText) => {
		// The line contains ONLY dashes - check surrounding lines for ASCII art
		const beforeMatch = fullText.substring(Math.max(0, offset - 200), offset);
		const afterMatch = fullText.substring(offset + match.length, Math.min(fullText.length, offset + match.length + 200));

		// Get the line before (last non-empty line before the dashes)
		const beforeLines = beforeMatch.split(/<br\s*\/?>/i).filter((l: string) => l.trim().length > 0);
		const lineBefore = beforeLines.length > 0 ? beforeLines[beforeLines.length - 1] : '';

		// Get the line after (first non-empty line after the dashes)
		const afterLines = afterMatch.split(/<br\s*\/?>/i).filter((l: string) => l.trim().length > 0);
		const lineAfter = afterLines.length > 0 ? afterLines[0] : '';

		// Check if surrounding lines have ASCII art patterns (excessive special chars)
		// ASCII art has lots of: -_=|+*/\ and spaces together
		const asciiArtPattern = /[-_=|+*\/\\\s]{5,}/;
		const hasAsciiArtBefore = lineBefore.match(asciiArtPattern);
		const hasAsciiArtAfter = lineAfter.match(asciiArtPattern);

		// If surrounding lines look like ASCII art, keep as dashes
		if (hasAsciiArtBefore || hasAsciiArtAfter) {
			if (ENGINE_LOGGING_ENABLED) {
				console.log('[processTextForOutput] Keeping dashes (ASCII art detected)', {
					lineBefore,
					lineAfter,
				});
			}
			return match;
		}

		// Convert to <hr> - standalone dash-only line
		if (ENGINE_LOGGING_ENABLED) {
			console.log('[processTextForOutput] Converting dashes to <hr>', {
				match,
				beforeBr,
				dashes,
				afterBr,
				offset,
				fullTextSample: fullText.substring(Math.max(0, offset - 50), Math.min(fullText.length, offset + match.length + 50)),
			});
		}
		return '<hr>';
	});

	// Remove <br> immediately after <hr> (only this specific case)
	processed = processed.replace(/(<hr\b[^>]*>)\s*<br\s*\/?>/gi, '$1');
	processed = processed.replace(/(<hr\b[^>]*>)\s*<br\s*\/?>/gi, '$1'); // Do it twice to catch multiple

	// Remove <br> immediately before <hr>
	processed = processed.replace(/<br\s*\/?>\s*(<hr\b[^>]*>)/gi, '$1');

	// Handle first append - remove leading <br> ONLY if this is the very first content
	// AND it's a single <br> at the start
	if (isFirstAppend) {
		// Only remove if it's a single leading <br> (not multiple)
		processed = processed.replace(/^(<br\s*\/?>)(?!<br)/i, '');
	}

	if (ENGINE_LOGGING_ENABLED) {
		console.log('[processTextForOutput] Result:', {
			processed: processed,
			processedLength: processed.length,
		});
	}

	return processed;
}

/**
 * True when the last non-whitespace node of the main text is a turn separator,
 * so a new turn header must not add a second one.
 */
export function mainTextEndsWithTurnSeparator(mainTextEl: HTMLElement): boolean {
	let node = mainTextEl.lastChild;
	while (node && node.nodeType === Node.TEXT_NODE && !(node.textContent || '').trim()) {
		node = node.previousSibling;
	}
	return !!node && node.nodeType === Node.ELEMENT_NODE && (node as Element).matches(TURN_SEPARATOR_SELECTOR);
}

function getLastTurnSeparator(mainTextEl: HTMLElement): HTMLElement | null {
	const separators = mainTextEl.querySelectorAll(TURN_SEPARATOR_SELECTOR);
	if (!separators.length) return null;
	return separators[separators.length - 1] as HTMLElement;
}

export function maybeAppendTurnSeparatorForInput(minDistanceToBottom = 160): void {
	const mainTextEl = $dom('#MainText').get(0) as HTMLElement | null;
	if (!mainTextEl) return;
	const separator = getLastTurnSeparator(mainTextEl);
	if (!separator) {
		const hr = document.createElement('hr');
		hr.className = 'turn-separator';
		mainTextEl.appendChild(hr);
		return;
	}
	const separatorBottom = separator.offsetTop + separator.offsetHeight;
	const distanceToBottom = mainTextEl.scrollHeight - separatorBottom;
	if (distanceToBottom > minDistanceToBottom) {
		const hr = document.createElement('hr');
		hr.className = 'turn-separator';
		mainTextEl.appendChild(hr);
	}
}

export function scrollMainTextToTurnStart(options?: { defer?: boolean }): void {
	const mainTextEl = $dom('#MainText').get(0) as HTMLElement | null;
	if (!mainTextEl) return;
	const run = () => {
		const separator = getLastTurnSeparator(mainTextEl);
		if (!separator) {
			if ((window as any).mainTextScrollbar) {
				(window as any).mainTextScrollbar.update();
			}
			return;
		}
		mainTextEl.scrollTop = separator.offsetTop;
		if ((window as any).mainTextScrollbar) {
			(window as any).mainTextScrollbar.update();
		}
	};
	if (options?.defer) {
		setTimeout(run, 0);
	} else {
		run();
	}
}

// Track if MainText is empty (for first append)
let mainTextIsEmpty = true;

/**
 * Escape HTML special characters
 */
export function escapeHtmlSpecialCharacters(str: string): string {
	const tagsToReplace: { [key: string]: string } = {
		'&': '&amp;',
		'<': '&lt;',
		'>': '&gt;',
	};
	return str.replace(/[&<>]/g, (tag) => {
		return tagsToReplace[tag] || tag;
	});
}

/**
 * Find nth index of pattern in string
 */
export function nthIndex(str: string, pat: string, n: number): number {
	let i = -1;
	while (n >= 0 && i++ < str.length) {
		i = str.indexOf(pat, i);
		if (i < 0) break;
		n--;
	}
	return i;
}

/**
 * Get array index from variable name
 */
export function GetArrayIndex(varname: string, n: number): string | number {
	let retval: string | number = -1;
	const index = nthIndex(varname, '(', n);
	if (index > -1) {
		const endindex = varname.indexOf(')', index);
		if (endindex > -1) {
			const indexvalue = varname.substring(index + 1, endindex);
			try {
				// parseInt() is less strict than System.Convert.ToInt32(),
				// so make sure this is a number (and not a range like "0..20"
				// which appears in some games)
				if(indexvalue.match(/^\s*[-\+]?\s*\d+\s*$/)) {
					retval = parseInt(indexvalue);
				}
			} catch (err) {
				retval = -1;
			}
		}
	}

	return retval;
}

/**
 * Get object class name
 */
export function getObjectClass(obj: any): string | undefined {
	if (obj instanceof Command) return 'command';

	// Check for Condition/ragscondition - need to check if constructor exists first
	const ConditionClass = (window as any).Condition || (window as any).ragscondition;
	if (ConditionClass && obj instanceof ConditionClass) return 'ragscondition';
	if (obj instanceof Condition) return 'ragscondition';

	if (obj instanceof Action) return 'action';

	if (typeof obj !== 'object') return undefined;

	if (obj && obj.constructor && obj.constructor.toString) {
		const arr = obj.constructor.toString().match(/function\s*(\w+)/);
		if (arr && arr.length == 2) {
			return arr[1];
		}
	}
	return undefined;
}

/**
 * Check if item is in inventory
 */
export function CheckItemInInventory(tempobj: RagsObject): boolean {
	if (!TheGame) return false;
	const foundObj = TheGame.Objects.filter((obj: any) => {
		return obj.name == tempobj.locationname;
	})[0];

	if (!foundObj) {
		return false;
	}

	if (foundObj.locationtype == 'LT_PLAYER') {
		return true;
	} else if (foundObj.locationtype == 'LT_IN_OBJECT') {
		return CheckItemInInventory(foundObj);
	} else {
		return false;
	}
}

/**
 * Get custom choice action
 */
export function GetCustomChoiceAction(type: string, name: string, actionname: string): Action | undefined {
	let tempact: Action | undefined = undefined;
	if (type == 'Chr') {
		const char = Finder.character(name);
		if (char) tempact = Finder.action(char.Actions, actionname);
	} else if (type == 'Obj') {
		const tempobj = Finder.object(name);
		if (tempobj != null)
			tempact = Finder.action(tempobj.Actions, actionname);
	} else if (type == 'Player') {
		if (TheGame) tempact = Finder.action(TheGame.Player.Actions, actionname);
	} else if (type == 'Room') {
		const temproom = Finder.room(name);
		if (temproom != null)
			tempact = Finder.action(temproom.Actions, actionname);
	} else if (type == 'Timer') {
		const temptimer = Finder.timer(name);
		if (temptimer != null)
			tempact = Finder.action(temptimer.Actions, actionname);
	}
	return tempact;
}

/**
 * Pause game
 */
export function PauseGame(): void {
	gameController.pause();
	const continueBtn = $dom('#Continue').get(0) as HTMLElement;
	if (continueBtn) {
		continueBtn.removeAttribute('disabled');
	}
}

/**
 * Pause game and return a promise that resolves when the game is continued
 */
export function PauseGameAsync(): Promise<void> {
	return gameController.awaitContinue();
}

/**
 * Test custom property
 */
export function TestCustomProperty(PropVal: string, step3: string, step4: string): boolean {
	let bResult = true;
	const replacedstring = (window as any).PerformTextReplacements?.(step4, null) || step4;
	let bIntComparison = false;
	let iPropVal = 0.0;
	let iReplacedString = 0.0;
	try {
		iPropVal = parseInt(PropVal, 10);
		iReplacedString = parseInt(replacedstring, 10);
		if (isNaN(iPropVal))
			bIntComparison = false;
		else
			bIntComparison = true;
	} catch (err) {
		bIntComparison = false;
	}
	if (step3 == 'Equals') {
		if (bIntComparison) {
			bResult = iReplacedString == iPropVal;
		} else {
			bResult = (replacedstring && replacedstring.toLowerCase()) == (PropVal && PropVal.toLowerCase());
		}
	} else if (step3 == 'Not Equals') {
		if (bIntComparison) {
			bResult = iReplacedString != iPropVal;
		} else {
			bResult = (replacedstring && replacedstring.toLowerCase()) != (PropVal && PropVal.toLowerCase());
		}
	} else if (step3 == 'Contains') {
		bResult = (PropVal.toLowerCase().indexOf(replacedstring && replacedstring.toLowerCase()) >= 0);
	} else if (step3 == 'Greater Than') {
		if (bIntComparison) {
			bResult = iPropVal > iReplacedString;
		} else {
			bResult = replacedstring > PropVal;
		}
	} else if (step3 == 'Greater Than or Equals') {
		if (bIntComparison) {
			bResult = iPropVal >= iReplacedString;
		} else {
			bResult = replacedstring >= PropVal;
		}
	} else if (step3 == 'Less Than') {
		if (bIntComparison) {
			bResult = iPropVal < iReplacedString;
		} else {
			bResult = replacedstring < PropVal;
		}
	} else if (step3 == 'Less Than or Equals') {
		if (bIntComparison) {
			bResult = iPropVal <= iReplacedString;
		} else {
			bResult = replacedstring <= PropVal;
		}
	}
	return bResult;
}

/**
 * Set array data
 */
export function SetArrayData(tempvar: Variable, resultval: any): void {
	tempvar.VarArray = resultval;
}

/**
 * Set variable value
 */
export async function SetVariable(tempvar: Variable, bArraySet: boolean, bJavascript: boolean, varindex: number | string, varindex1a: number | string, replacedstring: string, cmdtxt: string, part3: string): Promise<void> {
	varArrayCheck(tempvar, varindex, 'SetVariable');

	// Check if variable is frozen - if so, don't allow modification
	const cheatFreezes = (window as any).cheatFreezes;
	if (cheatFreezes && cheatFreezes.variables && cheatFreezes.variables[tempvar.varname]) {
		return; // Variable is frozen, don't modify it
	}

	const DateTimes = (window as any).DateTimes;
	if (tempvar.vartype == 'VT_DATETIMEARRAY' || tempvar.vartype == 'VT_DATETIME') {
		let dtDateTime = tempvar.dtDateTime;
		if (varindex != -1) {
			if (varindex1a != -1)
				dtDateTime = tempvar.VarArray![varindex as number][varindex1a as number];
			else
				dtDateTime = tempvar.VarArray![varindex as number];
		}

		const dateMoment = DateTimes?.stringDateToMoment?.(dtDateTime);
		if (dateMoment) {
			if (part3 == 'Add Days') {
				dateMoment.add(replacedstring, 'day');
			} else if (part3 == 'Add Hours') {
				dateMoment.add(replacedstring, 'hour');
			} else if (part3 == 'Add Minutes') {
				dateMoment.add(replacedstring, 'minute');
			} else if (part3 == 'Add Seconds') {
				dateMoment.add(replacedstring, 'second');
			} else if (part3 == 'Subtract Days') {
				dateMoment.subtract(replacedstring, 'day');
			} else if (part3 == 'Subtract Hours') {
				dateMoment.subtract(replacedstring, 'hour');
			} else if (part3 == 'Subtract Minutes') {
				dateMoment.subtract(replacedstring, 'minute');
			} else if (part3 == 'Subtract Seconds') {
				dateMoment.subtract(replacedstring, 'second');
			} else if (part3 == 'Set Day Of Month To') {
				dateMoment.date(parseInt(replacedstring, 10));
			} else if (part3 == 'Set Hours To') {
				dateMoment.hour(parseInt(replacedstring, 10));
			} else if (part3 == 'Set Minutes To') {
				dateMoment.minute(parseInt(replacedstring, 10));
			} else if (part3 == 'Set Seconds To') {
				dateMoment.second(parseInt(replacedstring, 10));
			} else if (part3 == 'Equals') {
				const newMoment = DateTimes?.stringDateToMoment?.(replacedstring);
				if (newMoment) {
					if (varindex != -1) {
						if (varindex1a != -1)
							tempvar.VarArray![varindex as number][varindex1a as number] = newMoment.format(DateTimes.defaultDateFormat);
						else
							tempvar.VarArray![varindex as number] = newMoment.format(DateTimes.defaultDateFormat);
					} else {
						tempvar.dtDateTime = newMoment.format(DateTimes.defaultDateFormat);
					}
				}
			}
			if (dateMoment) {
				if (varindex != -1) {
					if (varindex1a != -1)
						tempvar.VarArray![varindex as number][varindex1a as number] = dateMoment.format(DateTimes.defaultDateFormat);
					else
						tempvar.VarArray![varindex as number] = dateMoment.format(DateTimes.defaultDateFormat);
				} else {
					tempvar.dtDateTime = dateMoment.format(DateTimes.defaultDateFormat);
				}
			}
		}
	} else if (tempvar.vartype == 'VT_NUMBERARRAY' || tempvar.vartype == 'VT_NUMBER') {
		if (part3 == 'Equals') {
			if (bArraySet) {
				SetArrayData(tempvar, (window as any).jsresult);
			} else {
				const idx = typeof varindex === 'number' ? varindex : parseInt(String(varindex), 10);
				if (idx == -1)
					tempvar.dNumType = parseFloat(replacedstring);
				else {
					const idx1a = typeof varindex1a === 'number' ? varindex1a : parseInt(String(varindex1a), 10);
					if (idx1a != -1)
						tempvar.VarArray![idx][idx1a] = replacedstring;
					else
						tempvar.VarArray![idx] = parseFloat(replacedstring);
				}
			}
		} else if (part3 == 'Add') {
			const idx = typeof varindex === 'number' ? varindex : parseInt(String(varindex), 10);
			if (idx == -1)
				tempvar.dNumType += parseFloat(replacedstring);
			else {
				const idx1a = typeof varindex1a === 'number' ? varindex1a : parseInt(String(varindex1a), 10);
				if (idx1a != -1)
					tempvar.VarArray![idx][idx1a] = (parseFloat(String(tempvar.VarArray![idx][idx1a])) +
						parseFloat(replacedstring)).toString();
				else
					tempvar.VarArray![idx] = parseFloat(String(tempvar.VarArray![idx])) +
						parseFloat(replacedstring);
			}
		} else if (part3 == 'Subtract') {
			const idx = typeof varindex === 'number' ? varindex : parseInt(String(varindex), 10);
			if (idx == -1)
				tempvar.dNumType -= parseFloat(replacedstring);
			else {
				const idx1a = typeof varindex1a === 'number' ? varindex1a : parseInt(String(varindex1a), 10);
				if (idx1a != -1)
					tempvar.VarArray![idx][idx1a] = (parseFloat(String(tempvar.VarArray![idx][idx1a])) -
						parseFloat(replacedstring)).toString();
				else
					tempvar.VarArray![idx] = parseFloat(String(tempvar.VarArray![idx])) -
						parseFloat(replacedstring);
			}
		} else if (part3 == 'Multiply') {
			const idx = typeof varindex === 'number' ? varindex : parseInt(String(varindex), 10);
			if (idx == -1)
				tempvar.dNumType *= parseFloat(replacedstring);
			else {
				const idx1a = typeof varindex1a === 'number' ? varindex1a : parseInt(String(varindex1a), 10);
				if (idx1a != -1)
					tempvar.VarArray![idx][idx1a] = (parseFloat(String(tempvar.VarArray![idx][idx1a])) * parseFloat(replacedstring)).toString();
				else
					tempvar.VarArray![idx] = parseFloat(String(tempvar.VarArray![idx])) * parseFloat(replacedstring);
			}
		} else if (part3 == 'Divide') {
			const idx = typeof varindex === 'number' ? varindex : parseInt(String(varindex), 10);
			if (idx == -1)
				tempvar.dNumType /= parseFloat(replacedstring);
			else {
				const idx1a = typeof varindex1a === 'number' ? varindex1a : parseInt(String(varindex1a), 10);
				if (idx1a != -1)
					tempvar.VarArray![idx][idx1a] = (parseFloat(String(tempvar.VarArray![idx][idx1a])) / parseFloat(replacedstring)).toString();
				else
					tempvar.VarArray![idx] = parseFloat(String(tempvar.VarArray![idx])) / parseFloat(replacedstring);
			}
		}
		if (tempvar.bEnforceRestrictions) {
			const idx = typeof varindex === 'number' ? varindex : parseInt(String(varindex), 10);
			if (idx == -1) {
				const dMin = parseFloat((window as any).PerformTextReplacements?.(tempvar.dMin, null) || tempvar.dMin);
				const dMax = parseFloat((window as any).PerformTextReplacements?.(tempvar.dMax, null) || tempvar.dMax);
				if (tempvar.dNumType < dMin)
					tempvar.dNumType = dMin;
				if (tempvar.dNumType > dMax)
					tempvar.dNumType = dMax;
			} else {
				const idx1a = typeof varindex1a === 'number' ? varindex1a : parseInt(String(varindex1a), 10);
				if (idx1a != -1) {
					const dMin = parseFloat((window as any).PerformTextReplacements?.(tempvar.dMin, null) || tempvar.dMin);
					const dMax = parseFloat((window as any).PerformTextReplacements?.(tempvar.dMax, null) || tempvar.dMax);
					if (parseFloat(String(tempvar.VarArray![idx][idx1a])) < dMin)
						tempvar.VarArray![idx][idx1a] = String(dMin);
					if (parseFloat(String(tempvar.VarArray![idx][idx1a])) > dMax)
						tempvar.VarArray![idx][idx1a] = String(dMax);
				} else {
					const dMin = parseFloat((window as any).PerformTextReplacements?.(tempvar.dMin, null) || tempvar.dMin);
					const dMax = parseFloat((window as any).PerformTextReplacements?.(tempvar.dMax, null) || tempvar.dMax);
					if (parseFloat(String(tempvar.VarArray![idx])) < dMin)
						tempvar.VarArray![idx] = dMin;
					if (parseFloat(String(tempvar.VarArray![idx])) > dMax)
						tempvar.VarArray![idx] = dMax;
				}
			}
		}
	} else if (tempvar.vartype == 'VT_STRINGARRAY' || tempvar.vartype == 'VT_STRING') {
		let jsresult: any = null;
		if (bJavascript) {
			jsresult = await evalJankyJavascriptAsync(cmdtxt);
			if (!bArraySet && jsresult != null)
				cmdtxt = jsresult.toString();
		}
		if (bArraySet) {
			SetArrayData(tempvar, jsresult);
		} else {
			const idx = typeof varindex === 'number' ? varindex : parseInt(String(varindex), 10);
			if (idx == -1)
				tempvar.sString = cmdtxt;
			else {
				const idx1a = typeof varindex1a === 'number' ? varindex1a : parseInt(String(varindex1a), 10);
				if (idx1a != -1)
					tempvar.VarArray![idx][idx1a] = cmdtxt;
				else
					tempvar.VarArray![idx] = cmdtxt;
			}
		}
	}
}

/**
 * Set RAGS objects from JavaScript result
 */
export function SetRagsObjectsFromJavascript(resultval: any): void {
	const temparray = resultval;
	if (temparray != null) {
		for (let i = 0; i < temparray.length; i++) {
			const tempobj = temparray[i];
			if (tempobj.length > 0) {
				const objtomodify = '[' + tempobj[0].toString() + ']';
				let newval = tempobj[1].toString();
				if (newval == '')
					newval = ' ';
				(window as any).PerformTextReplacements?.(objtomodify, null, newval);
			}
		}
	}
}

/**
 * Check numeric limits
 */
export function CheckNumericLimits(tempvar: Variable, thevalue: string): number {
	//check min/max limits...
	if (tempvar.bEnforceRestrictions) {
		const themin = parseFloat((window as any).PerformTextReplacements?.(tempvar.dMin, null) || tempvar.dMin);
		const themax = parseFloat((window as any).PerformTextReplacements?.(tempvar.dMax, null) || tempvar.dMax);
		const val = parseFloat(thevalue);
		if (val < themin)
			return themin;
		if (val > themax)
			return themax;
		return val;
	} else
		return parseFloat(thevalue);
}

/**
 * Set command input
 */
export function SetCommandInput(tempcommand: Command, value: any): void {
	const part2 = (window as any).PerformTextReplacements?.(tempcommand.CommandPart2, null) || tempcommand.CommandPart2;
	const part3 = (window as any).PerformTextReplacements?.(tempcommand.CommandPart3, null) || tempcommand.CommandPart3;
	if (tempcommand.cmdtype === 'CT_REGALIA_ADVANCED_INPUT') {
		Globals.additionalData = value;
		const resolver = (Globals as any).advancedInputResolver;
		if (resolver) {
			(Globals as any).advancedInputResolver = null;
			(Globals as any).advancedInputReject = null;
			Globals.variableGettingSet = null;
			resolver(value);
		}
		return;
	}
	const tempvar = Finder.variable(part3);
	const varindex = GetArrayIndex(part3, 0);
	const varindex3a = GetArrayIndex(part3, 1);
	varArrayCheck(tempvar, varindex, 'SetCommandInput');
	if (tempvar != null) {
		switch (tempcommand.cmdtype) {
			case 'CT_SETVARIABLE_NUMERIC_BYINPUT': {
				const idx = typeof varindex === 'number' ? varindex : parseInt(String(varindex), 10);
				if (idx == -1) {
					tempvar.dNumType = CheckNumericLimits(tempvar, String(value));
				} else {
					const idx3a = typeof varindex3a === 'number' ? varindex3a : parseInt(String(varindex3a), 10);
					if (idx3a != -1) {
						tempvar.VarArray![idx][idx3a] = '';
						tempvar.VarArray![idx][idx3a] = CheckNumericLimits(tempvar, String(value)).toString();
					} else {
						tempvar.VarArray![idx] = '';
						tempvar.VarArray![idx] = CheckNumericLimits(tempvar, String(value)).toString();
					}
				}
				break;
			}
			case 'CT_SETVARIABLEBYINPUT': {
				const acttype = part2;
				const tempvar2 = Finder.variable(part3);
				const varindex2 = GetArrayIndex(part3, 0);
				const varindex3a2 = GetArrayIndex(part3, 1);
				let valueToSave: string;
				if (value instanceof RagsObject) {
					valueToSave = (value as RagsObject).name;
				} else if (acttype === 'Character' || acttype === 'ObjectOrCharacter') {
					const char = Finder.character(value);
					valueToSave = char ? char.Charname : String(value);
				} else {
					valueToSave = String(value);
				}
				if (tempvar2 != null) {
					const idx = typeof varindex2 === 'number' ? varindex2 : parseInt(String(varindex2), 10);
					if (idx == -1) {
						tempvar2.sString = valueToSave;
					} else {
						const idx3a = typeof varindex3a2 === 'number' ? varindex3a2 : parseInt(String(varindex3a2), 10);
						if (idx3a != -1) {
							tempvar2.VarArray![idx][idx3a] = '';
							tempvar2.VarArray![idx][idx3a] = valueToSave;
						} else {
							tempvar2.VarArray![idx] = '';
							tempvar2.VarArray![idx] = valueToSave;
						}
					}
				}
				break;
			}
		}
	}
}

/**
 * Set custom property
 */
export function SetCustomProperty(curprop: CustomProperty, part3: string, replacedstring: string, entityId?: string): void {
	// Check if property is frozen - if so, don't allow modification
	if (entityId) {
		const cheatFreezes = (window as any).cheatFreezes;
		if (cheatFreezes && cheatFreezes.customProperties && 
		    cheatFreezes.customProperties[entityId] && 
		    cheatFreezes.customProperties[entityId][curprop.Name]) {
			return; // Property is frozen, don't modify it
		}
	}
	
	let bInteger = true;
	const iReplacedString = parseFloat(replacedstring);
	const iPropVal = parseFloat(curprop.Value);
	if (isNaN(parseFloat(curprop.Value)) && isNaN(parseFloat(replacedstring))) {
		bInteger = false;
	}
	if (part3 == 'Equals') {
		curprop.Value = replacedstring;
	} else if (part3 == 'Add') {
		if (bInteger) {
			curprop.Value = (iReplacedString + iPropVal).toString();
		}
	} else if (part3 == 'Subtract') {
		if (bInteger) {
			curprop.Value = (iPropVal - iReplacedString).toString();
		}
	} else if (part3 == 'Multiply') {
		if (bInteger) {
			curprop.Value = (iReplacedString * iPropVal).toString();
		}
	} else if (part3 == 'Divide') {
		if (bInteger) {
			curprop.Value = (iPropVal / iReplacedString).toString();
		}
	}
}

/**
 * Update status bars
 */
export function UpdateStatusBars(): void {
	SetupStatusBars();
}

/**
 * Get exit from room by direction
 */
export function GetExit(room: Room, dir: string): any {
	for (let i = 0; i < room.Exits.length; i++) {
		if (room.Exits[i].Direction == dir) {
			return room.Exits[i];
		}
	}
	return null;
}

/**
 * Clone array for diff
 */
export function ArrayCloneForDiff(array: any[]): any[] {
	const result = new Array(array.length);
	for (let i = 0; i < array.length; ++i) {
		const el = array[i];
		if (el instanceof Object) {
			if ((el as any).cloneForDiff !== undefined) {
				result[i] = (el as any).cloneForDiff();
			} else if (Array.isArray(el)) {
				// variable.VarArray case
				result[i] = ArrayCloneForDiff(el);
			} else {
				result[i] = el;
			}
		} else {
			result[i] = el;
		}
	}

	return result;
}

export function varArrayCheck(variable: any, index1: number | string, prefix?: string): boolean {
	if (variable == undefined) return false;

	let msg = '';
	const isArray = variable.vartype.endsWith('ARRAY');
	const idx = typeof index1 === 'number' ? index1 : parseInt(String(index1), 10);
	if (idx == -1) {
		if (isArray) {
			if (prefix !== undefined && prefix.length > 0) {
				msg = '[' + prefix + '] ';
			}
			msg += `Variable "${variable.varname}" is ${variable.vartype} but used as ${variable.vartype.slice(0, -5)}`;
		}
	} else {
		if (!isArray) {
			if (prefix !== undefined && prefix.length > 0) {
				msg = prefix + ': ';
			}
			msg += `Variable "${variable.varname}" is ${variable.vartype} but used as ${variable.vartype + 'ARRAY'}`;
		}
	}

	if (msg.length > 0) {
		console.debug(msg);
		return false;
	}
	return true;
}

export function waitAsync(ms?: number): Promise<void> {
	if (ms === undefined) ms = 0;
	return new Promise((resolve) => {
		setTimeout(resolve, ms);
	});
}

/**
 * Clone game for diff
 */
export function GameCloneForDiff(game: any): any {
	// Only clone the properties that can change
	return {
		Rooms: ArrayCloneForDiff(game.Rooms),
		Player: game.Player.cloneForDiff(),
		Characters: ArrayCloneForDiff(game.Characters),
		Objects: ArrayCloneForDiff(game.Objects),
		Images: ArrayCloneForDiff(game.Images),
		Variables: ArrayCloneForDiff(game.Variables),
		Timers: ArrayCloneForDiff(game.Timers),
		StatusBarItems: ArrayCloneForDiff(game.StatusBarItems),
		bgMusic: game.bgMusic,
		TurnCount: (game as any).TurnCount,
	};
}

// Make functions globally available
(window as any).Interactables = Interactables;
(window as any).escapeQuotedTags = escapeQuotedTags;
(window as any).evalJankyJavascript = evalJankyJavascript;
(window as any).evalJankyJavascriptAsync = evalJankyJavascriptAsync;
(window as any).evalAdvancedScriptAsync = evalAdvancedScriptAsync;
(window as any).getJankyJavascriptExecutor = getJankyJavascriptExecutor;
(window as any).resetJankyJavascriptExecutor = resetJankyJavascriptExecutor;
(window as any).SetBorders = SetBorders;
(window as any).GetActionCount = GetActionCount;
(window as any).imagePath = imagePath;
(window as any).imageUrl = imageUrl;
(window as any).SetRoomThumb = SetRoomThumb;
(window as any).showImage = showImage;
(window as any).showLayeredPicture = showLayeredPicture;
(window as any).refreshRoomLayeredPicture = refreshRoomLayeredPicture;
(window as any).SetPortrait = SetPortrait;
(window as any).SetupStatusBars = SetupStatusBars;
(window as any).objectContainsObject = objectContainsObject;
(window as any).characterHasObject = characterHasObject;
(window as any).nameForAction = nameForAction;
(window as any).actionShouldBeVisible = actionShouldBeVisible;
(window as any).isLoopCheck = isLoopCheck;
(window as any).runNextAfterPause = runNextAfterPause;
(window as any).runAfterPause = runAfterPause;
(window as any).ChangeRoom = ChangeRoom;
(window as any).RoomChange = RoomChange;
(window as any).RoomChangeAsync = RoomChangeAsync;
(window as any).ChangeRoomAsync = ChangeRoomAsync;
(window as any).SetExits = SetExits;
(window as any).RefreshPictureBoxes = RefreshPictureBoxes;
(window as any).movePlayerToRoom = movePlayerToRoom;
(window as any).AddTextToRTF = AddTextToRTF;
(window as any).escapeHtmlSpecialCharacters = escapeHtmlSpecialCharacters;
(window as any).nthIndex = nthIndex;
(window as any).GetArrayIndex = GetArrayIndex;
(window as any).getObjectClass = getObjectClass;
(window as any).CheckItemInInventory = CheckItemInInventory;
(window as any).GetCustomChoiceAction = GetCustomChoiceAction;
(window as any).PauseGame = PauseGame;
(window as any).TestCustomProperty = TestCustomProperty;
(window as any).SetArrayData = SetArrayData;
(window as any).SetVariable = SetVariable;
(window as any).SetRagsObjectsFromJavascript = SetRagsObjectsFromJavascript;
(window as any).CheckNumericLimits = CheckNumericLimits;
(window as any).SetCommandInput = SetCommandInput;
(window as any).SetCustomProperty = SetCustomProperty;
(window as any).UpdateStatusBars = UpdateStatusBars;
(window as any).GetExit = GetExit;
(window as any).varArrayCheck = varArrayCheck;
(window as any).ArrayCloneForDiff = ArrayCloneForDiff;
(window as any).GameCloneForDiff = GameCloneForDiff;
(window as any).processTextForOutput = processTextForOutput;
